/**
 * The ITP register and its detail with the review loop.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * WORK_ORDER_M3_CONFIGURATION_AND_SURFACE.md section 4.2 screen 2. The register is the
 * register: every plan this principal may see, state chips from the server's states, and
 * nothing about the ones it may not. The detail carries the items, the typed review
 * exchange, the phase history and the planner's candidate list that approval fed.
 *
 * THE TYPED COMMENT LOOP
 *   Review moves are COMMENT, CONSULT, ACCEPT and OBJECT, and which of those a principal
 *   may record is the policy rows, not this screen. The screen offers COMMENT to both
 *   sides because the shipped pack gives COMMENT to both; a move the server refuses
 *   shows its refusal text. An internal note (CONSULT) renders with its visibility
 *   marker exactly when the server says the entry was internal.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router";

import { getTaxonomies, listContracts } from "../api/portfolio";

import {
  concurItemComplete,
  createItp,
  createItem,
  declareItemComplete,
  deleteItem,
  editItem,
  emptyItemPayload,
  getItp,
  itemPayloadOf,
  listCandidates,
  listItems,
  listItpTransitions,
  listReview,
  listItps,
  recordReviewMove,
  transitionItp,
  type Candidate,
  type ItemInstance,
  type ItpItem,
  type ItpSummary,
  type ItpTransitionEntry,
  type ItemPayload,
  type ReviewEntry,
} from "../api/itps";
import { Plus } from "lucide-react";
import {
  getContract,
  type ContractSummary,
  type InterventionLevel,
  type Taxonomies,
  type TaxonomyToken,
} from "../api/portfolio";
import { listPlanningProjects } from "../api/schedules";
import { CalendarPlanDialog } from "../calendar/CalendarPage";
import { ApiError } from "../api/client";
import { HistoryButton } from "../components/HistoryDrawer";
import { BarChart } from "../charts/BarChart";
import { useToken } from "../auth/sessionContext";
import type { Me } from "../api/me";
import {
  attachToTarget,
  listTargetAttachments,
  uploadAttachment,
  type TargetAttachment,
} from "../api/attachments";
import { DownloadLink } from "./Inspections";
import { strings, term } from "../brand/strings";
import { ActionButton } from "@/components/ui/action-button";
import { ActionDialog as UiActionDialog } from "@/components/ui/action-dialog";
import { Button } from "@/components/ui/button";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { DialogFooter } from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import {
  AbsencePanel,
  Breadcrumbs,
  EmptyState,
  Facts,
  SkeletonFacts,
  SkeletonRows,
  RefusalBox,
  RegisterTh,
  Section,
  StateChip,
  day,
  filterSet,
  stamp,
  type HeaderSort,
} from "./parts";
import { useQuery, type QueryState } from "./useQuery";
import { ServerErrorScope, ServerField } from "../lib/serverErrors";
import {
  MAX_REQUEST_ITEMS,
  listRfis,
  raiseRfi,
  type RfiRaisePayload,
  type RfiSummary,
} from "../api/rfis";
import { FilePicker } from "@/components/ui/file-picker";
import { ActionDialog, ToastBar } from "../nav/ActionDialog";
import { QueueBanner } from "../components/QueueBanner";
import { CitationChips } from "../components/Citations";
import { probeConfigurationAccess } from "../api/capabilities";
import { QUEUE_PARAM, readItpReviewQueue } from "../nav/queues";

/**
 * The request states that no longer hold an item (verification/queries.py
 * RFI_SPENT_FOR_RAISE): a new request may be raised beside them.
 */
const RFI_SPENT_FOR_RAISE: readonly string[] = ["CANCELLED", "REJECTED", "WAIVED", "CLOSED"];

/** When a request was made, for picking an item's latest spent one. */
function rfiMoment(rfi: RfiSummary): string {
  return rfi.submittedAt ?? rfi.plannedDate;
}

/** A register card: its title, then its states or its DataTable (UX_SPEC 2.1). */
function RegisterCard({
  title,
  children,
}: {
  readonly title?: string | undefined;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="q-ui mb-(--q-s2) flex min-w-0 flex-col gap-3">
      {title !== undefined && (
        <h3 className="text-xs tracking-[0.16em] text-muted-foreground uppercase">{title}</h3>
      )}
      {children}
    </section>
  );
}

/** The ITP register columns: what each cell says in plain words. */
function itpRegisterColumns(): RegisterColumn<ItpSummary>[] {
  const copy = strings.itps;
  return [
    {
      id: "plan",
      label: copy.columnPlan,
      text: (row) => `${row.humanId} ${row.title}`,
      sortValue: (row) => row.humanId,
      className: "min-w-[14rem]",
      render: (row) => (
        <>
          <Link
            to={`/itps/${row.id}`}
            className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline"
          >
            {row.humanId}
          </Link>
          <div className="max-w-[34rem] [overflow-wrap:anywhere] text-word">{row.title}</div>
        </>
      ),
    },
    {
      id: "contract",
      label: copy.columnContract,
      text: (row) => row.contractHumanId,
      sortValue: (row) => row.contractHumanId,
      className: "font-mono text-xs whitespace-nowrap",
    },
    {
      id: "discipline",
      label: copy.columnDiscipline,
      text: (row) => row.disciplineLabel,
      filter: true,
    },
    {
      id: "contractor",
      label: copy.columnContractor,
      text: (row) => row.contractorPartyName,
      filter: true,
    },
    {
      id: "items",
      label: copy.columnItems,
      text: (row) => String(row.itemCount),
      sortValue: (row) => row.itemCount,
      className: "text-right tabular-nums",
    },
    {
      id: "revision",
      label: copy.columnRevision,
      text: (row) => String(row.revision),
      sortValue: (row) => row.revision,
      className: "text-right tabular-nums",
    },
    {
      id: "state",
      label: copy.columnState,
      text: (row) => term(row.workflowState),
      filter: true,
      render: (row) => <StateChip state={row.workflowState} />,
    },
  ];
}

export function ItpsPage(): React.JSX.Element {
  const token = useToken();
  // The shell's Outlet context carries the signed-in principal; the create affordance
  // is the contractor's alone (action contract: an actor who may never perform it sees
  // nothing), and the server still refuses any wrong-actor write in place.
  const { me } = useOutletContext<{ me: Me }>();
  const taxonomies = useQuery(() => getTaxonomies(token), [token]);
  // Exactly the contracts the server accepts a new plan on (wave W0): the main
  // contractor's own MAIN contracts in a state that takes new work. Same statement as the
  // create check, so the dropdown never offers what the server refuses.
  const contracts = useQuery(() => listContracts(token, { itpAuthoring: true }), [token]);
  const [createdStamp, setCreatedStamp] = useState(0);
  // The chart contract's click-through lands here as ?state=: the register reads its
  // state filter from the URL, so browser back restores it and charts can deep-link it.
  const [parameters, setParameters] = useSearchParams();
  const actParam = parameters.get("act");
  const stateFilter = parameters.get("state") ?? (actParam === "rfi" ? "APPROVED" : null);
  const contractFilter = parameters.get("contractId") ?? parameters.get("contract");
  const [createOpen, setCreateOpen] = useState(
    actParam === "create" || parameters.get("create") === "true",
  );
  const [freeRfiOpen, setFreeRfiOpen] = useState(
    actParam === "rfi" || parameters.get("rfi") === "true",
  );
  const [toast, setToast] = useState<string | null>(null);

  // Multi-select column filters, comma-joined in the URL (M5 P6 seat V #6/#8).
  // Wave W1 (R13): `?queue=review` opens the register as the review queue, through the
  // SAME read Home counts, so the queue lists exactly what its Home card counted.
  const reviewQueue = parameters.get("queue") === QUEUE_PARAM.itpReview;
  const register = useQuery(
    async (signal) =>
      reviewQueue
        ? { items: await readItpReviewQueue(token, signal) }
        : listItps(token, { contractId: contractFilter, signal }),
    [token, createdStamp, reviewQueue, contractFilter],
  );

  const disciplineSet = filterSet(parameters.get("discipline"));
  const stateSet = filterSet(stateFilter);

  const setFilter = (name: string, value: string | null): void => {
    const next = new URLSearchParams(parameters);
    next.delete("offset");
    if (value === null || value.length === 0) {
      next.delete(name);
    } else {
      next.set(name, value);
    }
    if (name === "state") {
      next.delete("queue");
    }
    setParameters(next);
  };

  const columns = useMemo(() => itpRegisterColumns(), []);

  const items = useMemo(() => {
    if (register.kind !== "ready") return [];
    return register.data.items.filter(
      (itp) =>
        (contractFilter === null || itp.contractId === contractFilter) &&
        (disciplineSet.length === 0 || disciplineSet.includes(itp.disciplineCode)) &&
        (stateSet.length === 0 || stateSet.includes(itp.workflowState)),
    );
  }, [register, contractFilter, disciplineSet, stateSet]);

  return (
    <>
      <header className="q-ui mb-(--q-s1) flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1 basis-[28rem]">
          <h2 className="q-shell__heading">{strings.itps.heading}</h2>
          <p className="q-shell__lede">{strings.itps.lede}</p>
        </div>
        {me.workflowClass === "contractor" && (
          <div
            className="flex flex-wrap items-center gap-3 sm:justify-end"
            data-testid="create-itp-actions"
          >
            <ActionButton
              data-testid="raise-rfi-open"
              disabledReason={null}
              onClick={() => {
                setFreeRfiOpen(true);
              }}
            >
              <Plus aria-hidden="true" />
              {strings.itps.raiseFreeRfi}
            </ActionButton>
            <ActionButton
              data-testid="create-itp-open"
              disabledReason={null}
              onClick={() => {
                setCreateOpen(true);
              }}
            >
              <Plus aria-hidden="true" />
              {strings.itps.createSubmit}
            </ActionButton>
          </div>
        )}
      </header>

      {toast !== null && (
        <ToastBar
          message={toast}
          onDone={() => {
            setToast(null);
          }}
        />
      )}

      {createOpen && me.workflowClass === "contractor" && (
        <CreateItpDialog
          open={createOpen}
          {...(contractFilter ? { initialContractId: contractFilter } : {})}
          onClose={() => {
            setCreateOpen(false);
          }}
          contracts={contracts.kind === "ready" ? contracts.data.items : []}
          disciplines={taxonomies.kind === "ready" ? taxonomies.data.disciplines : []}
          onCreated={() => {
            setCreatedStamp((n) => n + 1);
          }}
        />
      )}

      {freeRfiOpen && me.workflowClass === "contractor" && (
        <RaiseFreeRfiDialog
          open={freeRfiOpen}
          {...(contractFilter ? { initialContractId: contractFilter } : {})}
          onClose={() => {
            setFreeRfiOpen(false);
          }}
          contracts={contracts.kind === "ready" ? contracts.data.items : []}
          disciplines={taxonomies.kind === "ready" ? taxonomies.data.disciplines : []}
          onRaised={(humanId) => {
            setFreeRfiOpen(false);
            setToast(strings.itps.rfiSent(humanId));
            setCreatedStamp((n) => n + 1);
          }}
        />
      )}

      {reviewQueue && (
        <QueueBanner
          label={strings.queues.itpReview.label}
          count={register.kind === "ready" ? register.data.items.length : null}
          wholeRegister="/itps"
        />
      )}

      {register.kind === "ready" && (
        <BarChart
          ariaLabel={`${strings.itps.heading} · ${strings.common.status}`}
          frameClassName="q-chart__frame--lead"
          tickAngle={-35}
          data={[...new Set(register.data.items.map((row) => row.workflowState))].map((state) => ({
            label: term(state),
            value: register.data.items.filter((row) => row.workflowState === state).length,
          }))}
          onSelectDatum={(datum) => {
            const match = register.data.items.find(
              (row) => term(row.workflowState) === datum.label,
            );
            if (match !== undefined) {
              setFilter("state", match.workflowState);
            }
          }}
        />
      )}

      <RegisterCard>
        {register.kind === "loading" && <SkeletonRows />}
        {register.kind === "absence" && <AbsencePanel />}
        {register.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {register.kind === "ready" &&
          (items.length === 0 ? (
            <EmptyState title={strings.itps.emptyTitle} body={strings.itps.emptyBody} />
          ) : (
            <DataTable
              data={items}
              columns={columns}
              caption={strings.itps.heading}
              testId="itps"
              getRowId={(row) => row.id}
            />
          ))}
      </RegisterCard>
    </>
  );
}

interface ReviewFormState {
  body: string;
  busy: boolean;
  error: ApiError | null;
}

/**
 * The parties an item's intervention editor offers, read from the tenant's taxonomy:
 * the operator's party type (the internal one) and every counterparty type, each with
 * its own level. Nothing here names a level or a party type: the taxonomy does.
 */
export interface InterventionChoices {
  readonly operator: TaxonomyToken | null;
  readonly others: readonly TaxonomyToken[];
  readonly levels: readonly InterventionLevel[];
}

export function interventionChoices(taxonomies: Taxonomies): InterventionChoices {
  const withId = taxonomies.partyTypes.filter((one) => one.id !== undefined);
  return {
    operator: withId.find((one) => one.isInternal === true) ?? null,
    others: withId.filter((one) => one.isInternal !== true),
    levels: taxonomies.interventionLevels,
  };
}

/** The order the editor shows the parties in: the operator first. */
export function partyOrder(choices: InterventionChoices | null): string[] {
  if (choices === null) {
    return [];
  }
  return [choices.operator, ...choices.others].flatMap((one) =>
    one?.id === undefined ? [] : [one.id],
  );
}

/** One labelled field of the item form; the control and its ServerField are the child. */
function ItemTextField({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="q-field">
      <label className="q-field__label" htmlFor={id}>
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * One item's content and its interventions, shared by the add form and the edit form.
 *
 * Wave R0 Break 3: the operator's intervention is chosen here, mandatory on create, and
 * every other party type the taxonomy exposes may add its own level. A level the
 * operator does not attend is offered (the taxonomy holds it) but says plainly that the
 * plan cannot be submitted with it, which is the server's submit guard said in advance.
 */
function ItemForm({
  heading,
  payload,
  disabled,
  requireOperator,
  choices,
  onChange,
  onSubmit,
  onCancel,
}: {
  heading: string;
  payload: ItemPayload;
  disabled: boolean;
  /** Create demands the operator's level; an edit of an older item may lack one. */
  requireOperator: boolean;
  /** The taxonomy's parties and levels, or its loading or failed state. */
  choices: QueryState<InterventionChoices>;
  onChange: (payload: ItemPayload) => void;
  onSubmit: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const ready = choices.kind === "ready" ? choices.data : null;
  const operatorId = ready?.operator?.id ?? null;
  const operatorLevel = operatorId === null ? "" : (payload.interventions[operatorId] ?? "");
  const chosenLevel = ready?.levels.find((one) => one.token === operatorLevel) ?? null;
  const attended = (ready?.levels ?? []).filter((one) => one.requiresOperatorAttendance);
  const missingOperator = requireOperator && operatorLevel === "";
  const order = partyOrder(ready);
  // The wire index of a party's row, so a refusal of `interventions.N` marks that row.
  const filled = order.filter((id) => (payload.interventions[id] ?? "") !== "");
  const setLevel = (partyTypeId: string, code: string): void => {
    onChange({ ...payload, interventions: { ...payload.interventions, [partyTypeId]: code } });
  };
  const textProps = (
    key: Exclude<keyof ItemPayload, "interventions">,
    id: string,
  ): {
    id: string;
    className: string;
    value: string;
    onChange: (event: { target: { value: string } }) => void;
  } => ({
    id,
    className: "q-input",
    value: payload[key],
    onChange: (event) => {
      onChange({ ...payload, [key]: event.target.value });
    },
  });
  return (
    <form
      className="q-form"
      aria-label={heading}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <p className="q-field__label">{heading}</p>
      {/* Each field names its own wire field literally: the named-field gate
        (lib/serverErrors.test.tsx) reads these to prove every field the server can
        refuse is on the form. */}
      <ItemTextField id="itp-item-no" label={strings.itps.itemNo}>
        <ServerField field="item_no">
          <input {...textProps("itemNo", "itp-item-no")} />
        </ServerField>
      </ItemTextField>
      <ItemTextField id="itp-item-activity" label={strings.itps.activity}>
        <ServerField field="activity_description">
          <input {...textProps("activityDescription", "itp-item-activity")} />
        </ServerField>
      </ItemTextField>
      <ItemTextField id="itp-item-method" label={strings.itps.method}>
        <ServerField field="inspection_method">
          <input {...textProps("inspectionMethod", "itp-item-method")} />
        </ServerField>
      </ItemTextField>
      <ItemTextField id="itp-item-acceptance" label={strings.itps.acceptance}>
        <ServerField field="acceptance_criteria">
          <input {...textProps("acceptanceCriteria", "itp-item-acceptance")} />
        </ServerField>
      </ItemTextField>
      <ItemTextField id="itp-item-reference" label={strings.itps.referenceDocument}>
        <ServerField field="reference_document">
          <input {...textProps("referenceDocument", "itp-item-reference")} />
        </ServerField>
      </ItemTextField>
      <ItemTextField id="itp-item-verifying" label={strings.itps.verifyingDocument}>
        <ServerField field="verifying_document">
          <input {...textProps("verifyingDocument", "itp-item-verifying")} />
        </ServerField>
      </ItemTextField>
      <ItemTextField id="itp-item-remarks" label={strings.itps.remarks}>
        <ServerField field="remarks">
          <textarea {...textProps("remarks", "itp-item-remarks")} className="q-textarea" />
        </ServerField>
      </ItemTextField>
      <ItemTextField id="itp-item-sequence" label={strings.itps.sequence}>
        <ServerField field="sequence">
          <input {...textProps("sequence", "itp-item-sequence")} type="number" min={1} />
        </ServerField>
      </ItemTextField>

      <fieldset className="q-field" data-testid="item-interventions">
        <legend className="q-field__label">{strings.itps.interventionsLegend}</legend>
        {choices.kind === "loading" && (
          <p className="q-status" role="status" data-testid="item-interventions-loading">
            {strings.itps.interventionsLoading}
          </p>
        )}
        {(choices.kind === "failed" ||
          choices.kind === "absence" ||
          (ready !== null && (ready.operator === null || ready.levels.length === 0))) && (
          <p className="q-note" role="alert" data-testid="item-interventions-unavailable">
            {strings.itps.interventionsUnavailable}
          </p>
        )}
        {ready?.operator?.id !== undefined && ready.levels.length > 0 && (
          <>
            <div className="q-field">
              <label className="q-field__label" htmlFor="itp-item-operator-level">
                {strings.itps.operatorIntervention(ready.operator.label)}
                {requireOperator ? ` ${strings.itps.requiredMark}` : ""}
              </label>
              <ServerField field="interventions">
                <NativeSelect
                  id="itp-item-operator-level"
                  data-testid="item-operator-level"
                  required={requireOperator}
                  value={operatorLevel}
                  onChange={(event) => {
                    setLevel(ready.operator?.id ?? "", event.target.value);
                  }}
                >
                  <option value="">{strings.itps.interventionChoose}</option>
                  {ready.levels.map((level) => (
                    <option key={level.token} value={level.token}>
                      {strings.itps.interventionOption(level.label, level.notation)}
                    </option>
                  ))}
                </NativeSelect>
              </ServerField>
              <p className="q-note" data-testid="item-operator-hint">
                {chosenLevel !== null && !chosenLevel.requiresOperatorAttendance
                  ? strings.itps.operatorNotAttended(attended.map((one) => one.label))
                  : strings.itps.operatorHint(attended.map((one) => one.label))}
              </p>
            </div>
            {ready.others.length > 0 && (
              <p className="q-field__label">{strings.itps.otherInterventionsHeading}</p>
            )}
            {ready.others.map((party) => {
              const partyId = party.id ?? "";
              const selectId = `itp-item-level-${party.token.toLowerCase()}`;
              const wireIndex = filled.indexOf(partyId);
              return (
                <div className="q-field" key={partyId}>
                  <label className="q-field__label" htmlFor={selectId}>
                    {party.label}
                  </label>
                  <ServerField
                    field={`interventions.${String(wireIndex < 0 ? "none" : wireIndex)}`}
                  >
                    <NativeSelect
                      id={selectId}
                      data-testid={`item-level-${party.token}`}
                      value={payload.interventions[partyId] ?? ""}
                      onChange={(event) => {
                        setLevel(partyId, event.target.value);
                      }}
                    >
                      <option value="">{strings.itps.interventionNone}</option>
                      {ready.levels.map((level) => (
                        <option key={level.token} value={level.token}>
                          {strings.itps.interventionOption(level.label, level.notation)}
                        </option>
                      ))}
                    </NativeSelect>
                  </ServerField>
                </div>
              );
            })}
          </>
        )}
      </fieldset>

      {missingOperator && ready !== null && (
        <p className="q-note" data-testid="item-operator-required">
          {strings.itps.operatorRequired}
        </p>
      )}
      <div className="q-actions-row">
        <button
          type="submit"
          className="q-button q-button--quiet q-mini--primary"
          data-testid="item-save"
          disabled={disabled || payload.itemNo.length === 0 || missingOperator || ready === null}
        >
          {strings.itps.itemSave}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onCancel}>
          {strings.itps.itemCancel}
        </button>
      </div>
    </form>
  );
}

/**
 * The item's interventions in the register: the operator's level as a chip with its
 * notation (H, W, S, S/R, R, from the taxonomy), every other party's on a secondary
 * line, and the full labels on hover.
 */
export function InterventionCell({ item }: { item: ItpItem }): React.JSX.Element {
  const operator = item.interventions.find((one) => one.isInternal) ?? null;
  const others = item.interventions.filter((one) => !one.isInternal);
  return (
    <div data-testid="item-interventions-cell">
      {operator === null ? (
        <span className="q-chip q-chip--attention" data-testid="item-operator-chip-missing">
          {strings.itps.noOperatorIntervention}
        </span>
      ) : (
        <span
          className="q-chip"
          data-testid="item-operator-chip"
          title={strings.itps.interventionTitle(operator.partyTypeLabel, operator.label)}
          aria-label={strings.itps.interventionTitle(operator.partyTypeLabel, operator.label)}
        >
          {operator.notation}
        </span>
      )}
      {others.length > 0 && (
        <p
          className="q-note"
          data-testid="item-other-interventions"
          title={others
            .map((one) => strings.itps.interventionTitle(one.partyTypeLabel, one.label))
            .join("; ")}
        >
          {others.map((one) => `${one.partyTypeLabel} ${one.notation}`).join(" · ")}
        </p>
      )}
    </div>
  );
}

interface CreateFormState {
  title: string;
  contractId: string;
  disciplineId: string;
  busy: boolean;
  error: ApiError | null;
}

/**
 * The contractor's create affordance: title, contract and discipline, exactly the
 * payload ItpCreate demands. The contract dropdown lists the MAIN contracts the server
 * accepts a new plan on for this principal (the main-contractor doctrine: no
 * sub-contractor submission exists), and the author's party is never sent: the server
 * reads it from the token. A refusal renders in place.
 */
function CreateItpDialog({
  open,
  onClose,
  contracts,
  disciplines,
  onCreated,
  initialContractId,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly contracts: readonly ContractSummary[];
  readonly disciplines: readonly TaxonomyToken[];
  readonly onCreated: () => void;
  readonly initialContractId?: string;
}): React.JSX.Element {
  const token = useToken();
  const [form, setForm] = useState<CreateFormState>({
    title: "",
    contractId: initialContractId ?? "",
    disciplineId: "",
    busy: false,
    error: null,
  });

  useEffect(() => {
    if (initialContractId && form.contractId !== initialContractId) {
      setForm((prev) => (prev.contractId ? prev : { ...prev, contractId: initialContractId }));
    }
  }, [initialContractId, form.contractId]);

  const ready = form.title.length > 0 && form.contractId !== "" && form.disciplineId !== "";

  async function submit(): Promise<void> {
    setForm((prev) => ({ ...prev, busy: true, error: null }));
    try {
      await createItp(token, {
        contractId: form.contractId,
        disciplineId: form.disciplineId,
        title: form.title,
      });
      setForm({ title: "", contractId: "", disciplineId: "", busy: false, error: null });
      onCreated();
      onClose();
    } catch (error) {
      setForm((prev) => ({
        ...prev,
        busy: false,
        error: error instanceof ApiError ? error : null,
      }));
    }
  }

  return (
    <UiActionDialog open={open} title={strings.itps.createHeading} onClose={onClose}>
      <ServerErrorScope error={form.error}>
        <form
          className="space-y-4"
          data-testid="create-itp-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) {
              void submit();
            }
          }}
        >
          <Field>
            <Label htmlFor="itp-create-title">{strings.itps.createTitleLabel}</Label>
            <ServerField field="title">
              <Input
                id="itp-create-title"
                value={form.title}
                onChange={(event) => {
                  setForm((prev) => ({ ...prev, title: event.target.value }));
                }}
              />
            </ServerField>
          </Field>
          <Field>
            <Label htmlFor="itp-create-contract">{strings.itps.createContractLabel}</Label>
            <ServerField field="contract_id">
              <NativeSelect
                id="itp-create-contract"
                value={form.contractId}
                onChange={(event) => {
                  setForm((prev) => ({ ...prev, contractId: event.target.value }));
                }}
              >
                <option value="" />
                {contracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    {contract.humanId} ({contract.contractorPartyName})
                  </option>
                ))}
              </NativeSelect>
            </ServerField>
          </Field>
          <Field>
            <Label htmlFor="itp-create-discipline">{strings.itps.createDisciplineLabel}</Label>
            <ServerField field="discipline_id">
              <NativeSelect
                id="itp-create-discipline"
                value={form.disciplineId}
                onChange={(event) => {
                  setForm((prev) => ({ ...prev, disciplineId: event.target.value }));
                }}
              >
                <option value="" />
                {disciplines.map((discipline) => (
                  <option key={discipline.token} value={discipline.id ?? discipline.token}>
                    {discipline.label}
                  </option>
                ))}
              </NativeSelect>
            </ServerField>
          </Field>
          {form.error !== null && <RefusalBox error={form.error} />}
          <DialogFooter className="mt-4 flex flex-wrap justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              {strings.common.cancel}
            </Button>
            <Button
              type="submit"
              disabled={form.busy || !ready}
              title={!ready ? strings.actions.justificationDisabledReason : undefined}
            >
              {strings.itps.createSubmit}
            </Button>
          </DialogFooter>
        </form>
      </ServerErrorScope>
    </UiActionDialog>
  );
}

/** The lifecycle moves' plain labels; the action row and its dialog share them. */
const ITP_MOVE_LABELS: Record<"submit" | "approve" | "return" | "revise" | "cancel", string> = {
  submit: strings.itps.submitForReview,
  approve: strings.itps.approve,
  return: strings.itps.returnToContractor,
  revise: strings.itps.revise,
  cancel: strings.common.cancel,
};

export function ItpDetailPage(): React.JSX.Element {
  const token = useToken();
  // The shell's Outlet context carries the signed-in principal: the authoring
  // workspace (item CRUD + submit) is the owner contractor's alone, and only while
  // the plan is a DRAFT (a returned plan comes back as one). Owner-side principals
  // see everything read-only as before, and the server refuses any wrong write.
  const { me } = useOutletContext<{ me: Me }>();
  const { itpId = "" } = useParams();
  const [reload, setReload] = useState(0);
  const detail = useQuery(() => getItp(token, itpId), [token, itpId, reload]);
  const items = useQuery(() => listItems(token, itpId), [token, itpId, reload]);
  const review = useQuery(() => listReview(token, itpId), [token, itpId, reload]);
  const history = useQuery(() => listItpTransitions(token, itpId), [token, itpId, reload]);
  const candidates = useQuery(
    () => listCandidates(token, { unplannedOnly: false }),
    [token, reload],
  );

  const [form, setForm] = useState<ReviewFormState>({ body: "", busy: false, error: null });
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [addForm, setAddForm] = useState<ItemPayload | null>(null);
  const [editForm, setEditForm] = useState<{ id: string; payload: ItemPayload } | null>(null);
  const [itemError, setItemError] = useState<ApiError | null>(null);
  const [itemBusy, setItemBusy] = useState(false);
  // The action-dialog pattern (M5 P5e #8): the open lifecycle dialog's event, and the
  // timed toast the last submit left at the top of the page.
  const [dialogEvent, setDialogEvent] = useState<"submit" | "approve" | "return" | "revise" | null>(
    null,
  );
  const [toast, setToast] = useState<{ message: string; tone: "info" | "danger" } | null>(null);
  // Wave W2: the lifecycle dialog keeps its own refusal and asks for the return's reason.
  const [dialogError, setDialogError] = useState<ApiError | null>(null);
  const [dialogReason, setDialogReason] = useState("");
  const [dialogBusy, setDialogBusy] = useState(false);
  // Wave W2: the contractor's inspection request (RFI) from an accepted item.
  const [rfiItem, setRfiItem] = useState<ItpItem | null>(null);
  // Wave INST ruling 2: the item's completion in two hands.
  const [declaring, setDeclaring] = useState<ItpItem | null>(null);
  const [concurring, setConcurring] = useState<ItpItem | null>(null);
  const [planningCandidate, setPlanningCandidate] = useState<Candidate | null>(null);
  const [expandedItemThreads, setExpandedItemThreads] = useState<Set<string>>(() => new Set());
  const rfis = useQuery(() => listRfis(token, { itpId }), [token, itpId, reload]);
  // Wave R0: the item editor's parties and levels, from the tenant's taxonomy.
  // Wave STD ruling 12 (b12 frontend item 8): citing a clause from an item is a
  // CONFIGURE_RULES act, offered on the configuration capability the server answers.
  const configurer = useQuery(() => probeConfigurationAccess(token), [token]);
  const mayCite =
    me.workflowClass !== "contractor" &&
    me.workflowClass !== "tpi" &&
    configurer.kind === "ready" &&
    configurer.data;
  const taxonomies = useQuery(() => getTaxonomies(token), [token]);
  const choices: QueryState<InterventionChoices> =
    taxonomies.kind === "ready"
      ? { kind: "ready", data: interventionChoices(taxonomies.data) }
      : taxonomies;
  const choiceOrder = partyOrder(choices.kind === "ready" ? choices.data : null);

  const toggleItemThread = (itemId: string): void => {
    setExpandedItemThreads((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const isContractor = me.workflowClass === "contractor";
  // Wave W1: the third party inspector is a counterparty too, never a reviewer.
  const isCounterparty = isContractor || me.workflowClass === "tpi";
  const authoring =
    isContractor && detail.kind === "ready" && detail.data.workflowState === "DRAFT";

  // ---------------------------------------------------------------------------
  // THE ITEMIZED REVIEW LOOP (M5 P5g, his round-5 #6).
  //   The operator accepts or objects to EACH item, with a comment and a file;
  //   the plan-level Approve stays disabled while any item is still open; the
  //   authoring contractor answers each objection from the returned plan. The
  //   item actions ride the same backend verbs as the plan exchange: ACCEPT,
  //   OBJECT and the contractor's COMMENT reply, item-addressed.
  // ---------------------------------------------------------------------------
  const reviewing =
    !isCounterparty &&
    detail.kind === "ready" &&
    (detail.data.workflowState === "SUBMITTED" ||
      detail.data.workflowState === "UNDER_REVIEW" ||
      detail.data.workflowState === "ENDORSED");
  const answering =
    isContractor && detail.kind === "ready" && detail.data.workflowState === "DRAFT";
  const showItemActions = reviewing || answering;

  // The whole-plan gate, as the button's arithmetic: the count of the items that
  // are neither accepted nor withdrawn. The server refuses the same fact with
  // itp.items_not_accepted; the button never asks it to.
  const openItemCount =
    items.kind === "ready"
      ? items.data.items.filter((one) => !one.isAccepted && !one.isWithdrawn).length
      : 0;

  // The exchange, grouped: plan-level moves stay in the plan thread, item moves
  // ride under their own line.
  const itemMovesByItemId = new Map<string, ReviewEntry[]>();
  if (review.kind === "ready") {
    for (const entry of review.data) {
      if (entry.itpItemId !== null) {
        const thread = itemMovesByItemId.get(entry.itpItemId);
        if (thread === undefined) {
          itemMovesByItemId.set(entry.itpItemId, [entry]);
        } else {
          thread.push(entry);
        }
      }
    }
  }
  const planMoves: ReviewEntry[] =
    review.kind === "ready" ? review.data.filter((entry) => entry.itpItemId === null) : [];

  // The detail's items register (M5 P6 seat W #2): the columns sort, the item
  // status column filters (multi-select, check-marked), the table scrolls contained.
  const [itemSort, setItemSort] = useState<HeaderSort>({ key: null, dir: "asc" });
  const [itemStatusFilter, setItemStatusFilter] = useState<readonly string[]>([]);
  const [candidateSort, setCandidateSort] = useState<HeaderSort>({ key: null, dir: "asc" });
  const itemStatusOptions = useMemo(() => {
    if (items.kind !== "ready") {
      return [];
    }
    const seen = new Map<string, string>();
    for (const one of items.data.items) {
      if (!seen.has(one.status)) {
        seen.set(one.status, one.statusLabel || one.status);
      }
    }
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [items]);
  const visibleItems = useMemo(() => {
    if (items.kind !== "ready") {
      return [];
    }
    const rows = itemStatusFilter.length
      ? items.data.items.filter((one) => itemStatusFilter.includes(one.status))
      : [...items.data.items];
    const dir = itemSort.dir === "asc" ? 1 : -1;
    if (itemSort.key === "itemNo") {
      rows.sort((a, b) => dir * a.itemNo.localeCompare(b.itemNo, undefined, { numeric: true }));
    } else if (itemSort.key === "status") {
      rows.sort((a, b) => dir * a.status.localeCompare(b.status));
    }
    return rows;
  }, [items, itemSort, itemStatusFilter]);

  const [itemDialog, setItemDialog] = useState<{
    // AMEND (wave W2): an accepted item's Accept becomes an edit of the acceptance.
    kind: "ACCEPT" | "OBJECT" | "REPLY" | "AMEND";
    itemId: string;
    itemNo: string;
    replyToId: string | null;
  } | null>(null);
  const [itemMoveBody, setItemMoveBody] = useState("");
  // Wave W1.5 upload sweep: several files on one move; a second pick used to replace the
  // first. The picker appends and lists them, each removable.
  const [itemMoveFiles, setItemMoveFiles] = useState<File[]>([]);
  const [itemMoveBusy, setItemMoveBusy] = useState(false);
  const [itemMoveError, setItemMoveError] = useState<ApiError | null>(null);

  const reread = (): void => {
    setReload((n) => n + 1);
  };

  async function saveNewItem(): Promise<void> {
    if (addForm === null) {
      return;
    }
    setItemBusy(true);
    setItemError(null);
    try {
      await createItem(token, itpId, addForm, choiceOrder);
      setAddForm(null);
      reread();
    } catch (error) {
      if (error instanceof ApiError) {
        setItemError(error);
      }
    } finally {
      setItemBusy(false);
    }
  }

  async function saveEditedItem(): Promise<void> {
    if (editForm === null) {
      return;
    }
    setItemBusy(true);
    setItemError(null);
    try {
      await editItem(token, itpId, editForm.id, editForm.payload, choiceOrder);
      setEditForm(null);
      reread();
    } catch (error) {
      if (error instanceof ApiError) {
        setItemError(error);
      }
    } finally {
      setItemBusy(false);
    }
  }

  async function removeItem(itemId: string): Promise<void> {
    setItemBusy(true);
    setItemError(null);
    try {
      await deleteItem(token, itpId, itemId);
      reread();
    } catch (error) {
      if (error instanceof ApiError) {
        setItemError(error);
      }
    } finally {
      setItemBusy(false);
    }
  }

  async function move(event: "submit" | "approve" | "return" | "revise" | "cancel"): Promise<void> {
    setDialogBusy(true);
    setDialogError(null);
    setNotice(null);
    try {
      const outcome = await transitionItp(token, itpId, event, {
        comment: dialogReason.trim().length > 0 ? dialogReason.trim() : null,
      });
      if (event === "approve" && outcome.candidatesCreated > 0) {
        setNotice(strings.itps.approveSuccess(outcome.candidatesCreated));
      }
      setToast({
        message:
          event === "return"
            ? strings.itps.returnSent
            : strings.schedules.actionToastDone(ITP_MOVE_LABELS[event]),
        tone: "info",
      });
      setDialogEvent(null);
      reread();
    } catch (error) {
      // Wave W2: the refusal stays INSIDE the dialog, which stays open. Mahmoud's walk
      // read "a return or a cancellation is recorded with its reason" BEHIND it.
      if (error instanceof ApiError) {
        setDialogError(error);
      }
    } finally {
      setDialogBusy(false);
    }
  }

  function openDialog(event: "submit" | "approve" | "return" | "revise"): void {
    setDialogEvent(event);
    setDialogError(null);
    setDialogReason("");
  }

  async function recordComment(): Promise<void> {
    setForm({ ...form, busy: true, error: null });
    try {
      await recordReviewMove(token, itpId, {
        move: "COMMENT",
        body: form.body.length > 0 ? form.body : null,
        internal: false,
      });
      setForm({ body: "", busy: false, error: null });
      reread();
    } catch (error) {
      setForm({
        ...form,
        busy: false,
        error: error instanceof ApiError ? error : null,
      });
    }
  }

  /** One item-level move: the verb, its comment, then its file on the same move. */
  async function submitItemMove(): Promise<void> {
    if (itemDialog === null) {
      return;
    }
    setItemMoveBusy(true);
    setItemMoveError(null);
    try {
      if (itemDialog.kind === "AMEND") {
        // Wave W2: an edit of the acceptance, never a second acceptance. A note is a
        // reply naming the acceptance; files hang on the acceptance move itself.
        const acceptanceId = itemDialog.replyToId;
        if (acceptanceId === null) {
          return;
        }
        if (itemMoveBody.trim().length > 0) {
          await recordReviewMove(token, itpId, {
            move: "COMMENT",
            body: itemMoveBody.trim(),
            itpItemId: itemDialog.itemId,
            inReplyToId: acceptanceId,
            internal: false,
          });
        }
        for (const file of itemMoveFiles) {
          await attachToTarget(token, file, {
            entityType: "ITP_REVIEW_MOVE",
            entityId: acceptanceId,
            purpose: "EVIDENCE",
          });
        }
        setItemDialog(null);
        setItemMoveBody("");
        setItemMoveFiles([]);
        setToast({ message: strings.itps.editAcceptanceSaved, tone: "info" });
        reread();
        return;
      }
      const outcome = await recordReviewMove(token, itpId, {
        move: itemDialog.kind === "REPLY" ? "COMMENT" : itemDialog.kind,
        body: itemMoveBody.length > 0 ? itemMoveBody : null,
        itpItemId: itemDialog.itemId,
        inReplyToId: itemDialog.replyToId,
        internal: false,
      });
      for (const file of itemMoveFiles) {
        await attachToTarget(token, file, {
          entityType: "ITP_REVIEW_MOVE",
          entityId: outcome.id,
          purpose: "EVIDENCE",
        });
      }
      setItemDialog(null);
      setItemMoveBody("");
      setItemMoveFiles([]);
      setToast({
        // Wave W2: the screen says exactly what the move did to planning.
        message:
          outcome.candidate === "OPENED"
            ? strings.itps.candidateOpenedToast(itemDialog.itemNo)
            : outcome.candidate === "WITHDRAWN"
              ? strings.itps.candidateWithdrawnToast(itemDialog.itemNo)
              : strings.schedules.actionToastDone(term(outcome.move)),
        tone: "info",
      });
      reread();
    } catch (error) {
      if (error instanceof ApiError) {
        setItemMoveError(error);
      }
    } finally {
      setItemMoveBusy(false);
    }
  }

  if (detail.kind === "loading" || detail.kind === "failed") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.itps.detailHeading}</h2>
        {detail.kind === "loading" && <SkeletonFacts fields={6} />}
        {detail.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
      </>
    );
  }
  if (detail.kind === "absence") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.itps.detailHeading}</h2>
        <AbsencePanel />
      </>
    );
  }

  const itp: ItpSummary = detail.data;
  const plannedCandidates: Candidate[] =
    candidates.kind === "ready"
      ? candidates.data.items.filter((candidate) => candidate.itpId === itp.id)
      : [];
  // Wave W2: what each item's acceptance did to planning, read from the two registers
  // each side may read (the planner's candidates; the RFIs), never guessed.
  const candidateByItem = new Map(plannedCandidates.map((one) => [one.itpItemId, one]));
  // Wave R0 Break 9: an item's LIVE request (at most one, the server's rule) and, apart
  // from it, its latest SPENT one. A request that closed with rejected lines has handed
  // the item back as ACCEPTED, so the item may be requested again; the spent request is
  // still named on the line as history.
  const rfiByItem = new Map<string, RfiSummary>();
  const spentRfiByItem = new Map<string, RfiSummary>();
  for (const one of rfis.kind === "ready" ? rfis.data.items : []) {
    if (one.itpItemId === null || one.dateState === "CLOSED_WITHOUT_DATE") {
      continue;
    }
    if (!RFI_SPENT_FOR_RAISE.includes(one.workflowState)) {
      rfiByItem.set(one.itpItemId, one);
      continue;
    }
    const seen = spentRfiByItem.get(one.itpItemId);
    if (seen === undefined || rfiMoment(one) > rfiMoment(seen)) {
      spentRfiByItem.set(one.itpItemId, one);
    }
  }
  // The objections a return sends, with each item's latest reason (released moves only).
  const objected = (items.kind === "ready" ? items.data.items : [])
    .filter((one) => one.status === "COMMENTED")
    .map((item) => ({
      item,
      reason:
        [...(itemMovesByItemId.get(item.id) ?? [])]
          .reverse()
          .find((move) => move.move === "OBJECT" && move.visibility !== "INTERNAL")?.body ?? null,
    }));

  return (
    <ServerErrorScope error={itemError}>
      <Breadcrumbs trail={[{ label: strings.itps.heading, to: "/itps" }, { label: itp.humanId }]} />
      <h2 className="q-shell__heading" data-testid="itp-title">
        {itp.humanId} {itp.title}
      </h2>
      <p className="q-shell__lede">
        <StateChip state={itp.workflowState} />{" "}
        <span>
          {strings.itps.columnRevision} {String(itp.revision)}
        </span>
      </p>

      {toast !== null && (
        <ToastBar
          message={toast.message}
          tone={toast.tone}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      {notice !== null && (
        <div className="q-banner" role="status">
          <p className="q-thread__body">{notice}</p>
        </div>
      )}
      {/* The lifecycle actions live AT THE TOP (M5 P5e #8); each opens its dialog. */}
      <div className="q-actions-row q-gap-top" data-testid="itp-lifecycle">
        <HistoryButton objectType="ITP" objectId={itp.id} recordLabel={itp.humanId} />
        {/* The submit is the owner contractor's move (action contract: the operator
            may never perform it and sees no button); the server refuses a draft
            without items, and the refusal renders in place. */}
        {isContractor && itp.workflowState === "DRAFT" && (
          <button
            type="button"
            className="q-button q-button--quiet q-mini--primary"
            onClick={() => {
              openDialog("submit");
            }}
          >
            {strings.itps.submitForReview}
          </button>
        )}
        {/* Wave W1: the REVIEW_ITP task opens here, so approve and return show in every
            state the server accepts them from (SUBMITTED and UNDER_REVIEW), and only to
            the operator side (the contractor and the third party never review). */}
        {!isCounterparty &&
          (itp.workflowState === "SUBMITTED" || itp.workflowState === "UNDER_REVIEW") && (
            <>
              {/* The whole-plan gate: disabled with its reason until every item is
                green (the item set empty counts as green). The server refuses the
                same fact with itp.items_not_accepted. */}
              <button
                type="button"
                className="q-button q-button--quiet q-mini--primary"
                disabled={openItemCount > 0}
                aria-disabled={openItemCount > 0}
                data-testid="itp-approve-button"
                onClick={() => {
                  if (openItemCount === 0) {
                    openDialog("approve");
                  }
                }}
              >
                {strings.itps.approve}
              </button>
              {openItemCount > 0 && (
                <p className="q-note" data-testid="approve-blocked">
                  {strings.itps.approveBlocked(openItemCount)}
                </p>
              )}
              <button
                type="button"
                className="q-button q-button--quiet"
                onClick={() => {
                  openDialog("return");
                }}
              >
                {strings.itps.returnToContractor}
              </button>
            </>
          )}
        {itp.workflowState === "RETURNED" && (
          <button
            type="button"
            className="q-button q-button--quiet q-mini--primary"
            onClick={() => {
              openDialog("revise");
            }}
          >
            {strings.itps.revise}
          </button>
        )}
      </div>

      <div className="q-section-bands" data-testid="itp-detail-sections">
        <Section heading={strings.itps.detailHeading}>
          <Facts
            entries={[
              [strings.common.humanId, <span key="id">{itp.humanId}</span>],
              [strings.itps.columnContract, <span key="contract">{itp.contractHumanId}</span>],
              [strings.itps.columnDiscipline, <span key="discipline">{itp.disciplineLabel}</span>],
              [strings.itps.columnContractor, <span key="party">{itp.contractorPartyName}</span>],
              [
                strings.itps.columnItems,
                <span key="count">
                  {String(items.kind === "ready" ? items.data.total : itp.itemCount)}
                </span>,
              ],
            ]}
          />
        </Section>

        {dialogEvent !== null && (
          <ActionDialog
            open
            title={ITP_MOVE_LABELS[dialogEvent]}
            error={dialogError}
            onClose={() => {
              setDialogEvent(null);
            }}
          >
            <p className="q-shell__lede">
              {strings.schedules.actionConfirm(ITP_MOVE_LABELS[dialogEvent])}
            </p>
            {dialogEvent === "return" && (
              <>
                {/* Wave W2: the return IS the loop. The reviewer's item decisions travel to
                  the main contractor as its REVISE_ITP task; the dialog shows what goes. */}
                <p className="q-note" data-testid="return-objections-lede">
                  {strings.itps.returnObjectionsLede(objected.length)}
                </p>
                {objected.length > 0 && (
                  <ul className="q-note" data-testid="return-objections">
                    {objected.map(({ item, reason }) => (
                      <li key={item.id}>
                        {strings.itps.itemNo} {item.itemNo}: {reason ?? ""}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="q-field">
                  <label className="q-field__label" htmlFor="itp-return-reason">
                    {strings.itps.returnReasonLabel}
                  </label>
                  <ServerField field="comment">
                    <textarea
                      id="itp-return-reason"
                      className="q-textarea"
                      data-testid="itp-return-reason"
                      value={dialogReason}
                      onChange={(event) => {
                        setDialogReason(event.target.value);
                      }}
                    />
                  </ServerField>
                </div>
              </>
            )}
            <div className="q-actions-row">
              <button
                type="button"
                className="q-button q-mini--primary"
                data-testid="itp-dialog-confirm"
                disabled={
                  dialogBusy || (dialogEvent === "return" && dialogReason.trim().length === 0)
                }
                title={
                  dialogEvent === "return" && dialogReason.trim().length === 0
                    ? strings.itps.returnNeedsReason
                    : undefined
                }
                onClick={() => {
                  void move(dialogEvent);
                }}
              >
                {ITP_MOVE_LABELS[dialogEvent]}
              </button>
              <button
                type="button"
                className="q-button q-button--quiet"
                onClick={() => {
                  setDialogEvent(null);
                }}
              >
                {strings.common.cancel}
              </button>
            </div>
          </ActionDialog>
        )}

        {itemDialog !== null && (
          <ActionDialog
            open
            title={
              itemDialog.kind === "AMEND"
                ? strings.itps.editAcceptanceHeading(itemDialog.itemNo)
                : itemDialog.kind === "REPLY"
                  ? strings.itps.itemReply
                  : itemDialog.kind === "ACCEPT"
                    ? strings.itps.reviewMoveAccept
                    : strings.itps.reviewMoveObject
            }
            error={itemMoveError}
            onClose={() => {
              setItemDialog(null);
            }}
          >
            <p className="q-shell__lede" data-testid="item-move-dialog">
              {strings.itps.itemNo} {itemDialog.itemNo}
              {itemDialog.kind === "REPLY" && itemDialog.replyToId !== null && " · reply"}
            </p>
            {itemDialog.kind === "AMEND" && (
              <div data-testid="acceptance-record">
                <p className="q-note">{strings.itps.editAcceptanceLede}</p>
                <p className="q-field__label">{strings.itps.editAcceptanceRecorded}</p>
                {(itemMovesByItemId.get(itemDialog.itemId) ?? [])
                  .filter(
                    (one) =>
                      one.id === itemDialog.replyToId || one.inReplyToId === itemDialog.replyToId,
                  )
                  .map((one) => (
                    <article className="q-thread__entry" key={one.id}>
                      <p className="q-thread__meta">
                        <strong>{one.actorDisplayName}</strong> · {term(one.move)} · {stamp(one.at)}
                      </p>
                      {one.body !== null && <p className="q-thread__body">{one.body}</p>}
                      <MoveAttachments moveId={one.id} />
                    </article>
                  ))}
              </div>
            )}
            <div className="q-field">
              <label className="q-field__label" htmlFor="item-move-body">
                {itemDialog.kind === "AMEND"
                  ? strings.itps.editAcceptanceNoteLabel
                  : strings.itps.itemCommentLabel}
              </label>
              <ServerField field="body">
                <textarea
                  id="item-move-body"
                  className="q-textarea"
                  value={itemMoveBody}
                  onChange={(event) => {
                    setItemMoveBody(event.target.value);
                  }}
                />
              </ServerField>
            </div>
            <div className="q-field">
              <FilePicker
                files={itemMoveFiles}
                onChange={setItemMoveFiles}
                label={strings.itps.attachEvidence}
                testId="item-move-file"
                disabled={itemMoveBusy}
              />
            </div>
            <div className="q-actions-row">
              <button
                type="button"
                className="q-button q-mini--primary"
                data-testid="item-move-confirm"
                disabled={
                  itemMoveBusy ||
                  (itemDialog.kind === "AMEND" &&
                    itemMoveBody.trim().length === 0 &&
                    itemMoveFiles.length === 0)
                }
                title={
                  itemDialog.kind === "AMEND" &&
                  itemMoveBody.trim().length === 0 &&
                  itemMoveFiles.length === 0
                    ? strings.itps.editAcceptanceNeedsSomething
                    : undefined
                }
                onClick={() => {
                  void submitItemMove();
                }}
              >
                {itemDialog.kind === "AMEND"
                  ? strings.itps.editAcceptanceSave
                  : itemDialog.kind === "REPLY"
                    ? strings.itps.itemReply
                    : itemDialog.kind === "ACCEPT"
                      ? strings.itps.reviewMoveAccept
                      : strings.itps.reviewMoveObject}
              </button>
              <button
                type="button"
                className="q-button q-button--quiet"
                onClick={() => {
                  setItemDialog(null);
                }}
              >
                {strings.common.cancel}
              </button>
            </div>
          </ActionDialog>
        )}

        {declaring !== null && (
          <DeclareCompleteDialog
            itpId={itp.id}
            item={declaring}
            onClose={() => {
              setDeclaring(null);
            }}
            onDone={() => {
              setToast({ message: strings.itps.declared(declaring.itemNo), tone: "info" });
              setDeclaring(null);
              reread();
            }}
          />
        )}
        {concurring !== null && (
          <ConcurCompleteDialog
            itpId={itp.id}
            item={concurring}
            onClose={() => {
              setConcurring(null);
            }}
            onDone={() => {
              setToast({ message: strings.itps.concurred(concurring.itemNo), tone: "info" });
              setConcurring(null);
              reread();
            }}
          />
        )}
        {rfiItem !== null && (
          <RaiseRfiDialog
            plan={itp}
            item={rfiItem}
            planItems={items.kind === "ready" ? items.data.items : []}
            categories={taxonomies.kind === "ready" ? taxonomies.data.verificationTypes : []}
            holdWait={holdOrderingWait(rfiItem, items.kind === "ready" ? items.data.items : [])}
            onClose={() => {
              setRfiItem(null);
            }}
            onRaised={(humanId) => {
              setRfiItem(null);
              setToast({ message: strings.itps.rfiSent(humanId), tone: "info" });
              reread();
            }}
          />
        )}

        <Section heading={strings.itps.itemsHeading}>
          {items.kind === "ready" && items.data.items.length === 0 && (
            <EmptyState title={strings.common.empty} body={strings.itps.emptyBody} />
          )}
          {items.kind === "ready" && items.data.items.length > 0 && (
            <div className="q-register-viewport">
              <table className="q-register">
                <thead>
                  <tr>
                    <RegisterTh
                      label={strings.itps.itemNo}
                      sortKey="itemNo"
                      sort={itemSort}
                      onSort={(key) => {
                        setItemSort((current) =>
                          current.key === key && current.dir === "asc"
                            ? { key, dir: "desc" }
                            : { key, dir: "asc" },
                        );
                      }}
                    />
                    <th scope="col">{strings.itps.activity}</th>
                    <th scope="col">{strings.itps.method}</th>
                    <th scope="col">{strings.itps.acceptance}</th>
                    <th scope="col">{strings.itps.interventionColumn}</th>
                    <RegisterTh
                      label={strings.itps.itemStatus}
                      sortKey="status"
                      sort={itemSort}
                      onSort={(key) => {
                        setItemSort((current) =>
                          current.key === key && current.dir === "asc"
                            ? { key, dir: "desc" }
                            : { key, dir: "asc" },
                        );
                      }}
                      filter={{
                        values: itemStatusFilter,
                        options: itemStatusOptions,
                        onChange: setItemStatusFilter,
                      }}
                    />
                    {authoring && <th scope="col">{strings.nav.actions}</th>}
                    {showItemActions && <th scope="col">{strings.itps.itemReviewLabel}</th>}
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((entry: ItpItem) => {
                    const itemThread = itemMovesByItemId.get(entry.id) ?? [];
                    const lastObjection = [...itemThread]
                      .reverse()
                      .find((move) => move.move === "OBJECT");
                    const columnCount = 6 + (authoring ? 1 : 0) + (showItemActions ? 1 : 0);
                    return (
                      <Fragment key={entry.id}>
                        <tr data-testid="itp-item-row">
                          <td className="q-register__mono">{entry.itemNo}</td>
                          <td className="q-register__wrap">
                            {entry.activityDescription}
                            <div className="mt-1 flex flex-wrap items-center gap-1">
                              <HistoryButton
                                objectType="ITP_ITEM"
                                objectId={entry.id}
                                recordLabel={entry.itemNo}
                              />
                            </div>
                            {(entry.citations.length > 0 || mayCite) && (
                              <CitationChips
                                citations={entry.citations}
                                target={{ type: "ITP_ITEM", id: entry.id }}
                                recordLabel={strings.itps.citationRecord(entry.itemNo)}
                                mayEdit={mayCite}
                                testId="item-citations"
                                onChanged={(message) => {
                                  setToast({ message, tone: "info" });
                                  reread();
                                }}
                              />
                            )}
                          </td>
                          <td>{entry.inspectionMethod}</td>
                          <td className="q-register__wrap">{entry.acceptanceCriteria}</td>
                          <td>
                            <InterventionCell item={entry} />
                          </td>
                          <td>
                            <StateChip state={entry.status} label={entry.statusLabel} />
                            <PlanningLine
                              item={entry}
                              planState={itp.workflowState}
                              reviewer={!isCounterparty}
                              contractor={isContractor}
                              candidate={candidateByItem.get(entry.id) ?? null}
                              rfi={rfiByItem.get(entry.id) ?? null}
                              lastRfi={spentRfiByItem.get(entry.id) ?? null}
                              holdWait={holdOrderingWait(entry, items.data.items)}
                              onRaise={() => {
                                setRfiItem(entry);
                              }}
                            />
                            <CompletionLine
                              item={entry}
                              planState={itp.workflowState}
                              onDeclare={() => {
                                setDeclaring(entry);
                              }}
                              onConcur={() => {
                                setConcurring(entry);
                              }}
                            />
                          </td>
                          {authoring && (
                            <td>
                              <div className="q-actions-row">
                                <button
                                  type="button"
                                  className="q-button q-button--quiet"
                                  onClick={() => {
                                    setItemError(null);
                                    setEditForm({ id: entry.id, payload: itemPayloadOf(entry) });
                                    setAddForm(null);
                                  }}
                                >
                                  {strings.itps.itemEdit}
                                </button>
                                <button
                                  type="button"
                                  className="q-button q-button--quiet"
                                  onClick={() => {
                                    void removeItem(entry.id);
                                  }}
                                >
                                  {strings.itps.itemDelete}
                                </button>
                              </div>
                            </td>
                          )}
                          {showItemActions && (
                            <td>
                              <div className="q-actions-row" data-testid="item-review-actions">
                                {reviewing && entry.isAccepted && (
                                  <button
                                    type="button"
                                    className="q-button q-button--quiet q-mini--primary"
                                    data-testid="item-edit-acceptance"
                                    onClick={() => {
                                      setItemMoveBody("");
                                      setItemMoveFiles([]);
                                      setItemMoveError(null);
                                      setItemDialog({
                                        kind: "AMEND",
                                        itemId: entry.id,
                                        itemNo: entry.itemNo,
                                        replyToId:
                                          [...itemThread]
                                            .reverse()
                                            .find((one) => one.move === "ACCEPT")?.id ?? null,
                                      });
                                    }}
                                  >
                                    {strings.itps.editAcceptance}
                                  </button>
                                )}
                                {reviewing && (
                                  <>
                                    {!entry.isAccepted && (
                                      <button
                                        type="button"
                                        className="q-button q-button--quiet q-mini--primary"
                                        data-testid="item-accept"
                                        onClick={() => {
                                          setItemMoveBody("");
                                          setItemMoveFiles([]);
                                          setItemMoveError(null);
                                          setItemDialog({
                                            kind: "ACCEPT",
                                            itemId: entry.id,
                                            itemNo: entry.itemNo,
                                            replyToId: null,
                                          });
                                        }}
                                      >
                                        {strings.itps.reviewMoveAccept}
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      className="q-button q-button--quiet"
                                      data-testid="item-object"
                                      onClick={() => {
                                        setItemMoveBody("");
                                        setItemMoveFiles([]);
                                        setItemMoveError(null);
                                        setItemDialog({
                                          kind: "OBJECT",
                                          itemId: entry.id,
                                          itemNo: entry.itemNo,
                                          replyToId: null,
                                        });
                                      }}
                                    >
                                      {strings.itps.reviewMoveObject}
                                    </button>
                                  </>
                                )}
                                {answering && entry.status === "COMMENTED" && (
                                  <button
                                    type="button"
                                    className="q-button q-button--quiet q-mini--primary"
                                    data-testid="item-reply"
                                    onClick={() => {
                                      setItemMoveBody("");
                                      setItemMoveFiles([]);
                                      setItemMoveError(null);
                                      setItemDialog({
                                        kind: "REPLY",
                                        itemId: entry.id,
                                        itemNo: entry.itemNo,
                                        replyToId: lastObjection?.id ?? null,
                                      });
                                    }}
                                  >
                                    {strings.itps.itemReply}
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                        {/* The item's own review thread (M5 P5g #4): the moves that name
                          this line ride under it; plan-level ones stay in the plan
                          thread below. Collapsed by default, unfolded on demand. */}
                        <tr>
                          <td colSpan={columnCount}>
                            <div className="py-1">
                              <button
                                type="button"
                                className="q-button q-button--quiet text-xs"
                                data-testid="toggle-item-history"
                                aria-expanded={expandedItemThreads.has(entry.id)}
                                onClick={() => {
                                  toggleItemThread(entry.id);
                                }}
                              >
                                {expandedItemThreads.has(entry.id)
                                  ? strings.itps.hideItemHistory
                                  : strings.itps.showItemHistory(itemThread.length)}
                              </button>
                              <div
                                className={`q-thread ${expandedItemThreads.has(entry.id) ? "" : "hidden"}`}
                                data-testid="item-thread"
                                hidden={!expandedItemThreads.has(entry.id)}
                              >
                                {itemThread.length === 0 && (
                                  <p className="q-note">{strings.itps.itemThreadEmpty}</p>
                                )}
                                {itemThread.map((move) => (
                                  <article className="q-thread__entry" key={move.id}>
                                    <p className="q-thread__meta">
                                      <strong>{move.actorDisplayName}</strong> ·{" "}
                                      {move.actingPartyName} · {term(move.move)} · {stamp(move.at)}
                                      {move.visibility === "INTERNAL" && (
                                        <span className="q-chip q-chip--attention q-thread__badge">
                                          {strings.itps.internalNote}
                                        </span>
                                      )}
                                    </p>
                                    {move.body !== null && (
                                      <p className="q-thread__body">{move.body}</p>
                                    )}
                                    <MoveAttachments moveId={move.id} />
                                  </article>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* The contractor's authoring workspace: rows are added, edited and removed
            only while the plan is a DRAFT, then the workspace freezes to read-only
            and the review exchange takes over. Non-contractor principals see none of
            it: for them the section is the read-only register it always was. */}
          {isContractor && !authoring && (
            <p className="q-note" data-testid="item-workspace-frozen">
              {strings.itps.itemWorkspaceFrozen}
            </p>
          )}
          {authoring && (
            <div className="q-gap-top" data-testid="item-workspace">
              <p className="q-note">{strings.itps.itemWorkspaceEditable}</p>
              {itemError !== null && addForm === null && editForm === null && (
                <RefusalBox error={itemError} />
              )}
              {/* The item add/edit forms open in the ActionDialog (M5 P6 seat W #1);
                the page body carries the compact opener row only. */}
              {editForm === null && addForm === null && (
                <div className="q-actions-row">
                  <button
                    type="button"
                    className="q-button q-button--quiet q-mini--primary"
                    data-testid="item-add-open"
                    onClick={() => {
                      setItemError(null);
                      setAddForm(
                        emptyItemPayload(items.kind === "ready" ? items.data.items.length + 1 : 1),
                      );
                    }}
                  >
                    {strings.itps.itemAdd}
                  </button>
                </div>
              )}
              {editForm !== null && (
                <ActionDialog
                  open
                  title={strings.itps.itemEditHeading}
                  error={itemError ?? undefined}
                  onClose={() => {
                    setEditForm(null);
                  }}
                >
                  <ItemForm
                    heading={strings.itps.itemEditHeading}
                    disabled={itemBusy}
                    requireOperator={false}
                    choices={choices}
                    payload={editForm.payload}
                    onChange={(payload) => {
                      setEditForm({ ...editForm, payload });
                    }}
                    onSubmit={() => {
                      void saveEditedItem();
                    }}
                    onCancel={() => {
                      setEditForm(null);
                    }}
                  />
                </ActionDialog>
              )}
              {addForm !== null && editForm === null && (
                <ActionDialog
                  open
                  title={strings.itps.itemAddHeading}
                  error={itemError ?? undefined}
                  onClose={() => {
                    setAddForm(null);
                  }}
                >
                  <ItemForm
                    heading={strings.itps.itemAddHeading}
                    disabled={itemBusy}
                    requireOperator
                    choices={choices}
                    payload={addForm}
                    onChange={(payload) => {
                      setAddForm(payload);
                    }}
                    onSubmit={() => {
                      void saveNewItem();
                    }}
                    onCancel={() => {
                      setAddForm(null);
                    }}
                  />
                </ActionDialog>
              )}
            </div>
          )}
        </Section>

        <Section heading={strings.itps.reviewHeading}>
          {/* The review comment opens in the ActionDialog (M5 P6 seat W #1). */}
          <div className="q-actions-row" data-testid="review-comment-actions">
            <button
              type="button"
              className="q-button q-button--quiet q-mini--primary"
              data-testid="review-comment-open"
              onClick={() => {
                setCommentDialogOpen(true);
              }}
            >
              {strings.itps.recordComment}
            </button>
          </div>
          {commentDialogOpen && (
            <ActionDialog
              open
              title={strings.itps.recordComment}
              onClose={() => {
                setCommentDialogOpen(false);
              }}
            >
              <div className="q-field">
                <label className="q-field__label" htmlFor="review-comment">
                  {strings.itps.reviewCommentLabel}
                </label>
                <ServerField field="body">
                  <textarea
                    id="review-comment"
                    className="q-textarea"
                    value={form.body}
                    onChange={(event) => {
                      setForm({ ...form, body: event.target.value });
                    }}
                  />
                </ServerField>
              </div>
              {form.error !== null && <RefusalBox error={form.error} />}
              <div className="q-actions-row">
                <button
                  type="button"
                  className="q-button q-button--quiet q-mini--primary"
                  data-testid="review-comment-confirm"
                  disabled={form.busy}
                  onClick={() => {
                    setCommentDialogOpen(false);
                    void recordComment();
                  }}
                >
                  {strings.itps.recordComment}
                </button>
                <button
                  type="button"
                  className="q-button q-button--quiet"
                  onClick={() => {
                    setCommentDialogOpen(false);
                  }}
                >
                  {strings.common.cancel}
                </button>
              </div>
            </ActionDialog>
          )}

          <div className="q-thread" data-testid="review-thread">
            {/* The plan-level thread: the moves that name no item (M5 P5g #4). */}
            {planMoves.length === 0 && <p className="q-note">{strings.common.empty}</p>}
            {planMoves.map((entry: ReviewEntry) => (
              <article className="q-thread__entry" key={entry.id}>
                <p className="q-thread__meta">
                  <strong>{entry.actorDisplayName}</strong> · {entry.actingPartyName} ·{" "}
                  {term(entry.move)} · {stamp(entry.at)}
                  {entry.visibility === "INTERNAL" && (
                    <span className="q-chip q-chip--attention q-thread__badge">
                      {strings.itps.internalNote}
                    </span>
                  )}
                  {entry.itemNo !== null && (
                    <span>
                      {" "}
                      · {strings.itps.itemNo} {entry.itemNo}
                    </span>
                  )}
                </p>
                {entry.body !== null && <p className="q-thread__body">{entry.body}</p>}
              </article>
            ))}
          </div>
        </Section>

        {!isContractor && (
          <Section heading={strings.itps.candidatesHeading} lede={strings.itps.candidatesBody}>
            {plannedCandidates.length === 0 && (
              <EmptyState title={strings.common.empty} body={strings.itps.candidatesBody} />
            )}
            {plannedCandidates.length > 0 && (
              <div className="q-register-viewport">
                <table className="q-register">
                  <thead>
                    <tr>
                      <RegisterTh
                        label={strings.itps.itemNo}
                        sortKey="itemNo"
                        sort={candidateSort}
                        onSort={(key) => {
                          setCandidateSort((current) =>
                            current.key === key && current.dir === "asc"
                              ? { key, dir: "desc" }
                              : { key, dir: "asc" },
                          );
                        }}
                      />
                      <th scope="col">{strings.itps.activity}</th>
                      <th scope="col">{strings.itps.acceptance}</th>
                      <th scope="col">{strings.itps.candidateColumnState}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(candidateSort.key === "itemNo"
                      ? [...plannedCandidates].sort((a, b) => {
                          const dir = candidateSort.dir === "asc" ? 1 : -1;
                          return (
                            dir * a.itemNo.localeCompare(b.itemNo, undefined, { numeric: true })
                          );
                        })
                      : plannedCandidates
                    ).map((candidate) => (
                      <tr key={candidate.id} data-testid="candidate-row">
                        <td className="q-register__mono">{candidate.itemNo}</td>
                        <td className="q-register__wrap">
                          {candidate.activityDescription}
                          {candidate.plannedActivityId !== null && (
                            <div>
                              <Link
                                className="q-link"
                                to={`/activities/${candidate.plannedActivityId}`}
                              >
                                {strings.schedules.columnActivity}
                              </Link>
                            </div>
                          )}
                        </td>
                        <td className="q-register__wrap">{candidate.acceptanceCriteria}</td>
                        <td>
                          {candidate.plannedActivityId !== null ? (
                            <Link
                              className="q-link"
                              to={`/activities/${candidate.plannedActivityId}`}
                            >
                              {strings.itps.planningPlanned}
                            </Link>
                          ) : candidate.requestedRfiId !== null ? (
                            <div className="flex items-center justify-between gap-2">
                              <Link className="q-link" to={`/rfis/${candidate.requestedRfiId}`}>
                                {strings.itps.planningRequested}
                              </Link>
                              <button
                                type="button"
                                className="q-button q-button--quiet q-mini--primary"
                                data-testid="plan-candidate-button"
                                onClick={() => {
                                  setPlanningCandidate(candidate);
                                }}
                              >
                                {strings.itps.planCandidate}
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-2">
                              <span>{strings.itps.candidateWaiting}</span>
                              <button
                                type="button"
                                className="q-button q-button--quiet q-mini--primary"
                                data-testid="plan-candidate-button"
                                onClick={() => {
                                  setPlanningCandidate(candidate);
                                }}
                              >
                                {strings.itps.planCandidate}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        )}

        <Section heading={strings.itps.historyHeading}>
          {history.kind === "ready" && <PhaseHistory entries={history.data} />}
        </Section>
      </div>

      {planningCandidate !== null && (
        <PlanCandidateDialog
          candidate={planningCandidate}
          onClose={() => {
            setPlanningCandidate(null);
          }}
          onPlanned={(message) => {
            setPlanningCandidate(null);
            setToast({ message, tone: "info" });
            reread();
          }}
        />
      )}
    </ServerErrorScope>
  );
}

function PlanCandidateDialog({
  candidate,
  onClose,
  onPlanned,
}: {
  readonly candidate: Candidate;
  readonly onClose: () => void;
  readonly onPlanned: (message: string, scheduleId: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const projects = useQuery(() => listPlanningProjects(token), [token]);
  const contract = useQuery(
    () => getContract(token, candidate.contractId),
    [token, candidate.contractId],
  );

  const projectList = projects.kind === "ready" ? projects.data : [];
  const initialProjectId =
    contract.kind === "ready" ? contract.data.linkedProjectIds[0] : undefined;

  return (
    <CalendarPlanDialog
      projects={projectList}
      initialCandidate={candidate}
      {...(initialProjectId ? { initialProjectId } : {})}
      onClose={onClose}
      onPlanned={onPlanned}
    />
  );
}

/** The item states that settle an item: nothing is left to request on it. */
const SETTLED_ITEM_STATES: readonly string[] = ["CLEARED", "WAIVED"];

function isSettledInstance(one: ItemInstance): boolean {
  return one.state === "CLEARED" || one.state === "WAIVED";
}

/**
 * Wave INST ruling 2: one chip per instance the item knows, as the server reads it: its
 * label (or the whole item, for a request raised before labels), its state, and the
 * request it came from. Settled chips are quiet; an open one (owed a new request) is the
 * one in colour.
 */
function InstanceChips({ item }: { readonly item: ItpItem }): React.JSX.Element | null {
  if (item.instances.length === 0) {
    return null;
  }
  return (
    <ul
      className="q-instance-list"
      data-testid="item-instances"
      aria-label={strings.itps.instancesLabel}
    >
      {item.instances.map((one, index) => (
        <li
          key={`${one.label ?? ""}-${String(index)}`}
          className="q-instance"
          data-testid="item-instance"
          data-state={one.state}
        >
          <span className="q-instance__label">{one.label ?? strings.itps.instanceWholeItem}</span>{" "}
          <StateChip state={one.state} />
          {one.verificationId !== null && (
            <>
              {" "}
              <Link
                className="q-link q-register__mono"
                to={`/rfis/${one.verificationId}`}
                data-testid="item-instance-request"
              >
                {one.verificationHumanId ?? strings.rfis.open}
              </Link>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Wave INST ruling 2, the hold ordering in words: a later item waits for its preceding
 * hold point to be CLEARED, and a clean inspection no longer clears an item; the
 * concurrence does. The words name what the preceding hold point still waits for. The
 * server's DM-ITP-1 refusal stays the judge; this only says where the wait stands.
 */
export function holdOrderingWait(item: ItpItem, items: readonly ItpItem[]): string | null {
  const blocking = items
    .filter(
      (one) =>
        one.sequence < item.sequence &&
        !SETTLED_ITEM_STATES.includes(one.status) &&
        one.interventions.some(
          (point) =>
            point.isInternal &&
            (point.notation === "H" || point.code === "HOLD" || point.code === "H"),
        ),
    )
    .sort((a, b) => a.sequence - b.sequence)[0];
  if (blocking === undefined) {
    return null;
  }
  if (blocking.declaredCompleteAt !== null) {
    return strings.itps.holdWaitsForConcurrence(blocking.itemNo);
  }
  const known = blocking.instances.length;
  if (known > 0 && blocking.instances.every(isSettledInstance)) {
    return strings.itps.holdWaitsForDeclaration(blocking.itemNo);
  }
  return strings.itps.holdWaitsForClearance(blocking.itemNo);
}

/**
 * Wave INST ruling 2: the item's completion, in two hands. The contractor declares the
 * scope complete with its instance count; an operator holding ITP_ITEM ACCEPT concurs,
 * and only then is the item CLEARED. The server decides who may: each button is offered
 * only when the item's own flag says this caller's act would be taken (review ruling A,
 * `may_declare_complete` and `may_concur_complete`); the routes still decide.
 */
function CompletionLine({
  item,
  planState,
  onDeclare,
  onConcur,
}: {
  readonly item: ItpItem;
  readonly planState: string;
  readonly onDeclare: () => void;
  readonly onConcur: () => void;
}): React.JSX.Element | null {
  if (!item.becomesCandidate) {
    return null;
  }
  const settled = item.instances.filter(isSettledInstance).length;
  if (item.completionConcurredAt !== null) {
    return (
      <p className="q-note" data-testid="item-completion" data-stage="concurred">
        {strings.itps.completionConcurred(day(item.completionConcurredAt))}
      </p>
    );
  }
  const open =
    planState === "APPROVED" && item.isAccepted && !SETTLED_ITEM_STATES.includes(item.status);
  if (item.declaredCompleteAt !== null && item.declaredInstanceCount !== null) {
    return (
      <div className="q-note" data-testid="item-completion" data-stage="declared">
        {strings.itps.completionDeclared(
          item.declaredInstanceCount,
          settled,
          day(item.declaredCompleteAt),
        )}
        {open && (item.mayConcurComplete || item.mayDeclareComplete) && (
          <div className="q-actions-row">
            {item.mayConcurComplete && (
              <button
                type="button"
                className="q-button q-button--quiet q-mini--primary"
                data-testid="item-concur"
                onClick={onConcur}
              >
                {strings.itps.concurComplete}
              </button>
            )}
            {item.mayDeclareComplete && (
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="item-declare"
                onClick={onDeclare}
              >
                {strings.itps.declareAgain}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
  if (!open || item.instances.length === 0) {
    return null;
  }
  return (
    <div className="q-note" data-testid="item-completion" data-stage="undeclared">
      {strings.itps.completionSettled(settled, item.instances.length)}
      {item.mayDeclareComplete && (
        <div>
          <button
            type="button"
            className="q-button q-button--quiet"
            data-testid="item-declare"
            onClick={onDeclare}
          >
            {strings.itps.declareComplete}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The contractor declares the item's scope complete (wave INST ruling 2): how many
 * instances the item has. The server refuses a count below the instances on record,
 * the same declaration twice and an item with no operator point, in its own words.
 */
function DeclareCompleteDialog({
  itpId,
  item,
  onClose,
  onDone,
}: {
  readonly itpId: string;
  readonly item: ItpItem;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.JSX.Element {
  const token = useToken();
  const [count, setCount] = useState(
    String(Math.max(1, item.declaredInstanceCount ?? item.instances.length)),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const whole = /^\d+$/u.test(count.trim()) && Number(count.trim()) >= 1;

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await declareItemComplete(token, itpId, item.id, Number(count.trim()));
      onDone();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={strings.itps.declareHeading(item.itemNo)}
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">{strings.itps.declareLede}</p>
      <InstanceChips item={item} />
      <div className="q-field">
        <label className="q-field__label" htmlFor="declare-count">
          {strings.itps.declareCountLabel}
        </label>
        <ServerField field="declared_instance_count">
          <input
            id="declare-count"
            className="q-input"
            type="number"
            inputMode="numeric"
            min="1"
            step="1"
            data-testid="declare-count"
            value={count}
            onChange={(event) => {
              setCount(event.target.value);
            }}
          />
        </ServerField>
      </div>
      {!whole && (
        <p className="q-note" data-testid="declare-count-hint">
          {strings.itps.declareCountHint}
        </p>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="declare-send"
          disabled={busy || !whole}
          onClick={() => {
            void send();
          }}
        >
          {strings.itps.declareSend}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

/**
 * The operator concurs (wave INST ruling 2): every known instance cleared or waived and
 * as many settled as declared. The dialog shows the chips; the server judges and names
 * any instance still open in its refusal, said inside the dialog.
 */
function ConcurCompleteDialog({
  itpId,
  item,
  onClose,
  onDone,
}: {
  readonly itpId: string;
  readonly item: ItpItem;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.JSX.Element {
  const token = useToken();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const settled = item.instances.filter(isSettledInstance).length;

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await concurItemComplete(token, itpId, item.id);
      onDone();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={strings.itps.concurHeading(item.itemNo)}
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">{strings.itps.concurLede}</p>
      <p className="q-note" data-testid="concur-counts">
        {strings.itps.completionDeclared(
          item.declaredInstanceCount ?? 0,
          settled,
          day(item.declaredCompleteAt ?? ""),
        )}
      </p>
      <InstanceChips item={item} />
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="concur-send"
          disabled={busy}
          onClick={() => {
            void send();
          }}
        >
          {strings.itps.concurComplete}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

/**
 * Wave W2: what an item's acceptance does to planning, said exactly. The panel used to
 * say "Feeds planning" beside every hold point while nothing happened until the whole
 * plan was approved. Now: a hold or witness item becomes a planning candidate AT ITS
 * ACCEPTANCE, the line says so, and it then says where the date stands: waiting, planned
 * as an activity, or requested by the contractor's RFI (which needs the approved plan).
 */
function PlanningLine({
  item,
  planState,
  reviewer,
  contractor,
  candidate,
  rfi,
  lastRfi = null,
  holdWait = null,
  onRaise,
}: {
  readonly item: ItpItem;
  readonly planState: string;
  readonly reviewer: boolean;
  readonly contractor: boolean;
  readonly candidate: Candidate | null;
  /** The item's live request, if one stands. */
  readonly rfi: RfiSummary | null;
  /** The item's latest spent request (closed, cancelled, rejected or waived). */
  readonly lastRfi?: RfiSummary | null;
  /** Wave INST: what the preceding hold point still waits for, when it holds this item. */
  readonly holdWait?: string | null;
  readonly onRaise: () => void;
}): React.JSX.Element | null {
  if (!item.becomesCandidate) {
    return null;
  }
  // Back to ACCEPTED after its request was spent (a close with rejections): requestable.
  const requestableAgain = rfi === null && lastRfi !== null && item.status === "ACCEPTED";
  const words = ((): React.ReactNode => {
    if (rfi === null && lastRfi !== null) {
      return (
        <>
          {requestableAgain ? strings.itps.planningRequestAgain : strings.itps.planningRequested}{" "}
          <Link className="q-link" to={`/rfis/${lastRfi.id}`}>
            {lastRfi.humanId}
          </Link>{" "}
          · {term(lastRfi.workflowState)}
        </>
      );
    }
    if (rfi !== null) {
      return (
        <>
          {strings.itps.planningRequested}{" "}
          <Link className="q-link" to={`/rfis/${rfi.id}`}>
            {rfi.humanId}
          </Link>{" "}
          · {day(rfi.plannedDate)} · {strings.rfis.dateState[rfi.dateState] ?? rfi.dateState}
        </>
      );
    }
    if (!item.isAccepted) {
      return reviewer
        ? strings.itps.planningWaitsForAcceptanceOperator
        : strings.itps.planningWaitsForAcceptanceContractor;
    }
    if (candidate?.requestedRfiId != null) {
      return (
        <>
          {strings.itps.planningRequested}{" "}
          <Link className="q-link" to={`/rfis/${candidate.requestedRfiId}`}>
            {strings.rfis.open}
          </Link>
        </>
      );
    }
    if (candidate?.plannedActivityId != null) {
      return (
        <>
          {strings.itps.planningPlanned}{" "}
          <Link className="q-link" to={`/activities/${candidate.plannedActivityId}`}>
            {strings.schedules.columnActivity}
          </Link>
        </>
      );
    }
    if (candidate !== null) {
      return strings.itps.planningCandidate(day(candidate.createdAt));
    }
    if (contractor) {
      return planState === "APPROVED"
        ? strings.itps.planningAcceptedContractorReady
        : strings.itps.planningAcceptedContractorWaits;
    }
    return null;
  })();
  // Wave INST ruling 2: the item is a template and its instances are requested in
  // parallel, so a standing request no longer holds the item; a settled item, or one the
  // operator planned as an activity, is not requested.
  const mayRaise =
    contractor &&
    item.isAccepted &&
    planState === "APPROVED" &&
    !SETTLED_ITEM_STATES.includes(item.status) &&
    candidate?.plannedActivityId == null;
  const another = rfi !== null || item.instances.length > 0;
  return (
    <div className="q-note q-planning-line" data-testid="planning-line">
      {words}
      <InstanceChips item={item} />
      {holdWait !== null && !SETTLED_ITEM_STATES.includes(item.status) && (
        <p className="q-note" data-testid="item-hold-wait">
          {holdWait}
        </p>
      )}
      {mayRaise && (
        <div>
          <button
            type="button"
            className="q-button q-button--quiet q-mini--primary"
            data-testid="item-raise-rfi"
            onClick={onRaise}
          >
            {another ? strings.itps.raiseAnotherInstance : strings.itps.raiseRfi}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Wave INST: what a request says beyond its scope and date. The instance it covers
 * (ruling 1: the item is the template, the request names its instance), the structured
 * pre-inspection declaration (ruling 3: the contractor's own check and its evidence) and,
 * once the server refused the date as short notice, the reason the inspection cannot
 * wait. The server judges all of it; the form only carries it.
 */
interface RfiExtras {
  readonly instanceLabel: string;
  readonly quantity: string;
  readonly externalAssetId: string;
  readonly internalCheckReference: string;
  readonly evidence: readonly File[];
  readonly justification: string;
}

const NO_RFI_EXTRAS: RfiExtras = {
  instanceLabel: "",
  quantity: "1",
  externalAssetId: "",
  internalCheckReference: "",
  evidence: [],
  justification: "",
};

/** The refusals that are about the instance label: the label control is marked. */
const INSTANCE_RULES: readonly string[] = [
  "rfi.instance_label_required",
  "rfi.already_requested",
  "rfi.instance_settled",
];

function ruleOf(error: ApiError | null): string | null {
  return error?.problem?.rule ?? null;
}

function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * The request's extra fields on the wire. Evidence is uploaded once per file (the cache
 * keeps the id, so a resubmission after a short-notice refusal does not upload again);
 * the justification travels only while the refused date stands.
 */
async function rfiExtrasPayload(
  token: string,
  extras: RfiExtras,
  withJustification: boolean,
  uploaded: Map<File, string>,
): Promise<Partial<RfiRaisePayload>> {
  const ids: string[] = [];
  for (const file of extras.evidence) {
    let id = uploaded.get(file);
    if (id === undefined) {
      id = await uploadAttachment(token, file);
      uploaded.set(file, id);
    }
    ids.push(id);
  }
  return {
    instance_label: textOrNull(extras.instanceLabel),
    quantity: extras.quantity.trim() === "" ? "1" : extras.quantity.trim(),
    external_asset_id: textOrNull(extras.externalAssetId),
    internal_check_reference: textOrNull(extras.internalCheckReference),
    ...(ids.length > 0 ? { attachment_ids: ids } : {}),
    ...(withJustification ? { short_notice_justification: extras.justification.trim() } : {}),
  };
}

/**
 * The short-notice turn (wave INST ruling 3): the server refused the date as inside the
 * notice lead time (409 rfi.short_notice, its sentence naming both numbers of days). The
 * dialog keeps that sentence, offers the justification, and resends with it while the
 * refused date stands; a new date drops the justification and is judged afresh.
 */
function useShortNotice(proposedDate: string): {
  readonly asked: boolean;
  readonly note: (error: ApiError) => void;
} {
  const [refusedDate, setRefusedDate] = useState<string | null>(null);
  return {
    asked: refusedDate !== null && refusedDate === proposedDate,
    note: (error) => {
      if (ruleOf(error) === "rfi.short_notice") {
        setRefusedDate(proposedDate);
      }
    },
  };
}

function RfiInstanceFields({
  prefix,
  required,
  extras,
  error,
  onChange,
}: {
  readonly prefix: string;
  /** An item request must name its instance; a free request may. */
  readonly required: boolean;
  readonly extras: RfiExtras;
  readonly error: ApiError | null;
  readonly onChange: (next: RfiExtras) => void;
}): React.JSX.Element {
  const labelRefused = INSTANCE_RULES.includes(ruleOf(error) ?? "");
  return (
    <>
      <div className="q-field">
        <label className="q-field__label" htmlFor={`${prefix}-instance`}>
          {required ? strings.itps.rfiInstanceLabel : strings.itps.rfiInstanceLabelOptional}
        </label>
        <p className="q-note" id={`${prefix}-instance-hint`}>
          {required ? strings.itps.rfiInstanceHint : strings.itps.rfiInstanceHintOptional}
        </p>
        <ServerField field="instance_label">
          <input
            id={`${prefix}-instance`}
            className="q-input"
            data-testid={`${prefix}-instance`}
            aria-required={required}
            aria-invalid={labelRefused ? true : undefined}
            aria-describedby={`${prefix}-instance-hint`}
            value={extras.instanceLabel}
            onChange={(event) => {
              onChange({ ...extras, instanceLabel: event.target.value });
            }}
          />
        </ServerField>
      </div>
      <div className="q-form-grid">
        <div className="q-field">
          <label className="q-field__label" htmlFor={`${prefix}-quantity`}>
            {strings.itps.rfiQuantityLabel}
          </label>
          <ServerField field="quantity">
            <input
              id={`${prefix}-quantity`}
              className="q-input"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              data-testid={`${prefix}-quantity`}
              title={strings.itps.rfiQuantityHint}
              value={extras.quantity}
              onChange={(event) => {
                onChange({ ...extras, quantity: event.target.value });
              }}
            />
          </ServerField>
        </div>
        <div className="q-field">
          <label className="q-field__label" htmlFor={`${prefix}-asset`}>
            {strings.itps.rfiAssetLabel}
          </label>
          <ServerField field="external_asset_id">
            <input
              id={`${prefix}-asset`}
              className="q-input"
              data-testid={`${prefix}-asset`}
              title={strings.itps.rfiAssetHint}
              value={extras.externalAssetId}
              onChange={(event) => {
                onChange({ ...extras, externalAssetId: event.target.value });
              }}
            />
          </ServerField>
        </div>
      </div>
    </>
  );
}

function RfiDeclarationFields({
  prefix,
  extras,
  declared,
  withEvidence,
  onChange,
  onDeclared,
}: {
  readonly prefix: string;
  readonly extras: RfiExtras;
  readonly declared: boolean;
  /** The free request keeps its own attachments list; the item request links evidence. */
  readonly withEvidence: boolean;
  readonly onChange: (next: RfiExtras) => void;
  readonly onDeclared: (declared: boolean) => void;
}): React.JSX.Element {
  return (
    <fieldset className="q-fieldset q-subform" data-testid={`${prefix}-declaration-block`}>
      <legend className="q-field__label">{strings.itps.rfiDeclarationHeading}</legend>
      <div className="q-field">
        <label className="q-field__label" htmlFor={`${prefix}-check-reference`}>
          {strings.itps.rfiCheckReferenceLabel}
        </label>
        <ServerField field="internal_check_reference">
          <input
            id={`${prefix}-check-reference`}
            className="q-input"
            data-testid={`${prefix}-check-reference`}
            title={strings.itps.rfiCheckReferenceHint}
            value={extras.internalCheckReference}
            onChange={(event) => {
              onChange({ ...extras, internalCheckReference: event.target.value });
            }}
          />
        </ServerField>
      </div>
      {withEvidence && (
        <div className="q-field">
          <span className="q-field__label">{strings.itps.rfiEvidenceLabel}</span>
          <ServerField field="attachment_ids">
            <FilePicker
              files={extras.evidence}
              onChange={(next) => {
                onChange({ ...extras, evidence: next });
              }}
              label={strings.itps.rfiEvidenceLabel}
              testId={`${prefix}-evidence`}
              emptyText={strings.itps.rfiEvidenceEmpty}
            />
          </ServerField>
        </div>
      )}
      <label className="q-check">
        <input
          type="checkbox"
          checked={declared}
          data-testid={`${prefix}-declaration`}
          onChange={(event) => {
            onDeclared(event.target.checked);
          }}
        />{" "}
        {strings.itps.rfiDeclaration}
      </label>
    </fieldset>
  );
}

function RfiShortNoticeField({
  prefix,
  extras,
  onChange,
}: {
  readonly prefix: string;
  readonly extras: RfiExtras;
  readonly onChange: (next: RfiExtras) => void;
}): React.JSX.Element {
  return (
    <div className="q-field" data-testid={`${prefix}-short-notice`}>
      <label className="q-field__label" htmlFor={`${prefix}-justification`}>
        {strings.itps.rfiShortNoticeLabel}
      </label>
      <p className="q-note">{strings.itps.rfiShortNoticeHint}</p>
      <ServerField field="short_notice_justification">
        <textarea
          id={`${prefix}-justification`}
          className="q-textarea"
          data-testid={`${prefix}-justification`}
          value={extras.justification}
          onChange={(event) => {
            onChange({ ...extras, justification: event.target.value });
          }}
        />
      </ServerField>
    </div>
  );
}

/** One row of the request's items list: the item, its instance, how much of it. */
interface RfiItemRow {
  readonly key: number;
  readonly itemId: string;
  readonly instanceLabel: string;
  readonly quantity: string;
}

/** The items a request may name: accepted, not withdrawn, not settled. */
function requestableItems(items: readonly ItpItem[]): ItpItem[] {
  return items.filter(
    (one) => one.isAccepted && !one.isWithdrawn && !SETTLED_ITEM_STATES.includes(one.status),
  );
}

/** A row's test ids: the first row's are bare (`rfi-instance`), later rows add `-N`. */
function rowId(base: string, index: number): string {
  return index === 0 ? base : `${base}-${String(index)}`;
}

/** The refusals about the items list as a whole: the shape the server judged. */
const ITEMS_RULES: readonly string[] = [
  "rfi.items_required",
  "rfi.items_too_many",
  "rfi.items_duplicate",
  "rfi.items_and_item",
  "rfi.items_cross_plan",
];

/**
 * The main contractor's inspection request on an approved plan (Mahmoud's R10; wave RFI
 * rulings 1 and 2): the PLAN it is raised on, the CHECKLIST CATEGORY it is conducted
 * under (optional: the tenant's RFI type by default), then the ITEMS list, one row per
 * item (the item, the instance it covers, the quantity), at most MAX_REQUEST_ITEMS rows,
 * starting from the item it was opened on. Then the scope, the date, the place and the
 * pre-inspection declaration. Every refusal stays inside the dialog in the server's words.
 */
function RaiseRfiDialog({
  plan,
  item,
  planItems,
  categories,
  holdWait = null,
  onClose,
  onRaised,
}: {
  readonly plan: { readonly id: string; readonly humanId: string };
  /** The item the request was opened on: the first row. */
  readonly item: ItpItem;
  /** The plan's items, from which the rows are chosen. */
  readonly planItems: readonly ItpItem[];
  /** The tenant's verification types: the checklist categories on offer. */
  readonly categories: readonly TaxonomyToken[];
  /** Wave INST: what the preceding hold point waits for, said with a DM-ITP-1 refusal. */
  readonly holdWait?: string | null;
  readonly onClose: () => void;
  readonly onRaised: (humanId: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const [category, setCategory] = useState("");
  const [rows, setRows] = useState<readonly RfiItemRow[]>([
    { key: 0, itemId: item.id, instanceLabel: "", quantity: "1" },
  ]);
  const [nextKey, setNextKey] = useState(1);
  const [scope, setScope] = useState(item.activityDescription);
  const [proposedDate, setProposedDate] = useState("");
  const [location, setLocation] = useState("");
  const [declared, setDeclared] = useState(false);
  const [extras, setExtras] = useState<RfiExtras>(NO_RFI_EXTRAS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const uploaded = useRef(new Map<File, string>());
  const shortNotice = useShortNotice(proposedDate);
  const offered = requestableItems(
    planItems.some((one) => one.id === item.id) ? planItems : [item, ...planItems],
  );
  const chosen = rows.map((row) => row.itemId).filter((id) => id !== "");
  const twice = new Set(chosen).size !== chosen.length;
  const missing = [
    ...rows.flatMap((row, index) => [
      row.itemId === "" ? strings.itps.rfiMissingItem(index + 1) : null,
      row.instanceLabel.trim() === "" ? strings.itps.rfiMissingInstance(index + 1) : null,
    ]),
    twice ? strings.itps.rfiItemTwice : null,
    scope.trim() === "" ? strings.fieldNames.scope : null,
    proposedDate === "" ? strings.fieldNames.proposed_date : null,
    shortNotice.asked && extras.justification.trim() === ""
      ? strings.fieldNames.short_notice_justification
      : null,
    declared ? null : strings.itps.rfiDeclarationMissing,
  ].filter((one): one is string => one !== null && one !== undefined);
  const ready = missing.length === 0;
  const full = rows.length >= MAX_REQUEST_ITEMS;
  const rule = ruleOf(error);
  // An instance refusal names its item: the rows of that item are marked.
  const refusedItemNos = INSTANCE_RULES.includes(rule ?? "")
    ? offered
        .filter((one) => (error?.problem?.detail ?? "").includes(one.itemNo))
        .map((one) => one.id)
    : [];

  function change(index: number, next: Partial<RfiItemRow>): void {
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...next } : row)));
  }

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const more = await rfiExtrasPayload(token, extras, shortNotice.asked, uploaded.current);
      const raised = await raiseRfi(token, {
        itp_id: plan.id,
        checklist_category: category === "" ? null : category,
        items: rows.map((row) => ({
          itp_item_id: row.itemId,
          instance_label: textOrNull(row.instanceLabel),
          quantity: row.quantity.trim() === "" ? "1" : row.quantity.trim(),
        })),
        scope: scope.trim(),
        proposed_date: proposedDate,
        location: location.trim().length > 0 ? location.trim() : null,
        declaration_confirmed: declared,
        external_asset_id: more.external_asset_id ?? null,
        internal_check_reference: more.internal_check_reference ?? null,
        ...(more.attachment_ids !== undefined ? { attachment_ids: more.attachment_ids } : {}),
        ...(more.short_notice_justification !== undefined
          ? { short_notice_justification: more.short_notice_justification }
          : {}),
      });
      onRaised(raised.humanId);
    } catch (caught) {
      if (caught instanceof ApiError) {
        shortNotice.note(caught);
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      wide
      title={
        rows.length === 1
          ? strings.itps.raiseRfiHeading(
              offered.find((one) => one.id === rows[0]?.itemId)?.itemNo ?? item.itemNo,
            )
          : strings.itps.raiseRfiPlanHeading(plan.humanId)
      }
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">{strings.itps.raiseRfiLede}</p>
      <div className="q-form-grid">
        <div className="q-field">
          <span className="q-field__label">{strings.itps.rfiPlanLabel}</span>
          <ServerField field="itp_id">
            <output id="rfi-plan" className="q-input" data-testid="rfi-plan">
              {plan.humanId}
            </output>
          </ServerField>
        </div>
        <div className="q-field">
          <label className="q-field__label" htmlFor="rfi-category">
            {strings.itps.rfiCategoryLabel}
          </label>
          <ServerField field="checklist_category">
            <select
              id="rfi-category"
              className="q-input"
              data-testid="rfi-category"
              title={strings.itps.rfiCategoryHint}
              value={category}
              onChange={(event) => {
                setCategory(event.target.value);
              }}
            >
              <option value="">{strings.itps.rfiCategoryDefault}</option>
              {categories.map((one) => (
                <option key={one.token} value={one.token}>
                  {one.label}
                </option>
              ))}
            </select>
          </ServerField>
        </div>
      </div>
      <ServerField field="items">
        <fieldset
          className="q-fieldset q-subform"
          id="rfi-items"
          data-testid="rfi-items"
          aria-invalid={ITEMS_RULES.includes(rule ?? "") ? true : undefined}
        >
          <legend className="q-field__label">{strings.itps.rfiItemsLegend}</legend>
          <p className="q-note">{strings.itps.rfiItemsHint(MAX_REQUEST_ITEMS)}</p>
          {rows.map((row, index) => (
            <div
              key={row.key}
              className="q-subform q-form-grid"
              data-testid="rfi-item-row"
              aria-label={strings.itps.rfiItemRow(index + 1)}
              role="group"
            >
              <p className="q-field__label" style={{ gridColumn: "1 / -1", margin: 0 }}>
                {strings.itps.rfiItemRow(index + 1)}
              </p>
              <div className="q-field">
                <label className="q-field__label" htmlFor={rowId("rfi-item", index)}>
                  {strings.itps.rfiItemLabel}
                </label>
                <ServerField field={`items.${String(index)}.itp_item_id`}>
                  <select
                    id={rowId("rfi-item", index)}
                    className="q-input"
                    data-testid={rowId("rfi-item", index)}
                    value={row.itemId}
                    onChange={(event) => {
                      change(index, { itemId: event.target.value });
                    }}
                  >
                    <option value="">{strings.itps.rfiItemChoose}</option>
                    {offered.map((one) => (
                      <option key={one.id} value={one.id}>
                        {strings.itps.rfiItemOption(one.itemNo, one.activityDescription)}
                      </option>
                    ))}
                  </select>
                </ServerField>
              </div>
              <div className="q-field">
                <label className="q-field__label" htmlFor={rowId("rfi-instance", index)}>
                  {strings.itps.rfiInstanceLabel}
                </label>
                <ServerField field={`items.${String(index)}.instance_label`}>
                  <input
                    id={rowId("rfi-instance", index)}
                    className="q-input"
                    data-testid={rowId("rfi-instance", index)}
                    aria-required="true"
                    aria-invalid={refusedItemNos.includes(row.itemId) ? true : undefined}
                    title={strings.itps.rfiInstanceHint}
                    value={row.instanceLabel}
                    onChange={(event) => {
                      change(index, { instanceLabel: event.target.value });
                    }}
                  />
                </ServerField>
              </div>
              <div className="q-field">
                <label className="q-field__label" htmlFor={rowId("rfi-quantity", index)}>
                  {strings.itps.rfiQuantityLabel}
                </label>
                <ServerField field={`items.${String(index)}.quantity`}>
                  <input
                    id={rowId("rfi-quantity", index)}
                    className="q-input"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="any"
                    data-testid={rowId("rfi-quantity", index)}
                    title={strings.itps.rfiQuantityHint}
                    value={row.quantity}
                    onChange={(event) => {
                      change(index, { quantity: event.target.value });
                    }}
                  />
                </ServerField>
              </div>
              {rows.length > 1 && (
                <div className="q-field">
                  <button
                    type="button"
                    className="q-button q-button--quiet"
                    data-testid={rowId("rfi-remove-item", index)}
                    onClick={() => {
                      setRows((current) => current.filter((_, at) => at !== index));
                    }}
                  >
                    {strings.itps.rfiRemoveItem}
                  </button>
                </div>
              )}
            </div>
          ))}
          <div className="q-actions-row">
            <button
              type="button"
              className="q-button q-button--quiet"
              data-testid="rfi-add-item"
              disabled={full}
              onClick={() => {
                setRows((current) => [
                  ...current,
                  { key: nextKey, itemId: "", instanceLabel: "", quantity: "1" },
                ]);
                setNextKey((one) => one + 1);
              }}
            >
              {strings.itps.rfiAddItem}
            </button>
            {full && (
              <p className="q-note" data-testid="rfi-items-full">
                {strings.itps.rfiItemsAtMost(MAX_REQUEST_ITEMS)}
              </p>
            )}
          </div>
        </fieldset>
      </ServerField>
      <div className="q-field">
        <label className="q-field__label" htmlFor="rfi-asset">
          {strings.itps.rfiAssetLabel}
        </label>
        <ServerField field="external_asset_id">
          <input
            id="rfi-asset"
            className="q-input"
            data-testid="rfi-asset"
            title={strings.itps.rfiAssetHint}
            value={extras.externalAssetId}
            onChange={(event) => {
              setExtras({ ...extras, externalAssetId: event.target.value });
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="rfi-scope">
          {strings.itps.rfiScopeLabel}
        </label>
        <ServerField field="scope">
          <textarea
            id="rfi-scope"
            className="q-textarea"
            data-testid="rfi-scope"
            value={scope}
            onChange={(event) => {
              setScope(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="rfi-date">
          {strings.itps.rfiDateLabel}
        </label>
        <ServerField field="proposed_date">
          <input
            id="rfi-date"
            className="q-input"
            type="date"
            data-testid="rfi-date"
            value={proposedDate}
            onChange={(event) => {
              setProposedDate(event.target.value);
            }}
          />
        </ServerField>
      </div>
      {shortNotice.asked && (
        <RfiShortNoticeField prefix="rfi" extras={extras} onChange={setExtras} />
      )}
      <div className="q-field">
        <label className="q-field__label" htmlFor="rfi-location">
          {strings.itps.rfiLocationLabel}
        </label>
        <ServerField field="location">
          <input
            id="rfi-location"
            className="q-input"
            value={location}
            onChange={(event) => {
              setLocation(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <RfiDeclarationFields
        prefix="rfi"
        extras={extras}
        declared={declared}
        withEvidence
        onChange={setExtras}
        onDeclared={setDeclared}
      />
      {!ready && (
        <p className="q-note" data-testid="rfi-missing">
          {strings.itps.rfiMissing(missing)}
        </p>
      )}
      {rule === "DM-ITP-1" && holdWait !== null && (
        <p className="q-note" data-testid="rfi-hold-wait">
          {holdWait}
        </p>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="rfi-send"
          disabled={busy || !ready}
          title={declared ? undefined : strings.itps.rfiDeclarationNeeded}
          onClick={() => {
            void send();
          }}
        >
          {shortNotice.asked ? strings.itps.rfiSendShortNotice : strings.itps.rfiSend}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

/**
 * The main contractor requests inspection under their contract without an ITP item:
 * scope text, discipline, proposed date, attachments. The form says plainly it is
 * a free request under the contract.
 */
export function RaiseFreeRfiDialog({
  open,
  onClose,
  onRaised,
  contracts: passedContracts,
  disciplines: passedDisciplines,
  initialContractId,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onRaised: (humanId: string) => void;
  readonly contracts?: readonly ContractSummary[];
  readonly disciplines?: readonly TaxonomyToken[];
  readonly initialContractId?: string;
}): React.JSX.Element {
  const token = useToken();
  const loadedContracts = useQuery(() => listContracts(token, { itpAuthoring: true }), [token]);
  const loadedTaxonomies = useQuery(() => getTaxonomies(token), [token]);
  const contracts =
    passedContracts ?? (loadedContracts.kind === "ready" ? loadedContracts.data.items : []);
  const disciplines =
    passedDisciplines ??
    (loadedTaxonomies.kind === "ready" ? loadedTaxonomies.data.disciplines : []);

  const [contractId, setContractId] = useState(
    initialContractId ?? (contracts.length === 1 ? (contracts[0]?.id ?? "") : ""),
  );
  const [disciplineId, setDisciplineId] = useState("");
  const [category, setCategory] = useState("");
  const categories =
    loadedTaxonomies.kind === "ready" ? loadedTaxonomies.data.verificationTypes : [];
  const engineeringOnly =
    loadedTaxonomies.kind === "ready" && loadedTaxonomies.data.freeRfiScope === "ENGINEERING_ONLY";
  const [scope, setScope] = useState("");
  const [proposedDate, setProposedDate] = useState("");
  const [location, setLocation] = useState("");
  const [declared, setDeclared] = useState(false);
  const [files, setFiles] = useState<readonly File[]>([]);
  const [extras, setExtras] = useState<RfiExtras>(NO_RFI_EXTRAS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const uploaded = useRef(new Map<File, string>());
  const shortNotice = useShortNotice(proposedDate);

  const selectedContractId =
    contractId !== ""
      ? contractId
      : (initialContractId ?? (contracts.length === 1 ? (contracts[0]?.id ?? "") : ""));

  const ready =
    selectedContractId !== "" &&
    disciplineId !== "" &&
    scope.trim().length > 0 &&
    proposedDate.length > 0 &&
    (!shortNotice.asked || extras.justification.trim() !== "") &&
    declared;

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const more = await rfiExtrasPayload(token, extras, shortNotice.asked, uploaded.current);
      const raised = await raiseRfi(token, {
        contract_id: selectedContractId,
        discipline_id: disciplineId,
        // Wave RFI ruling 3: a request without an item names its category; under
        // ENGINEERING_ONLY the server refuses one outside engineering, in its words.
        checklist_category: category === "" ? null : category,
        scope: scope.trim(),
        proposed_date: proposedDate,
        location: location.trim().length > 0 ? location.trim() : null,
        declaration_confirmed: declared,
        ...more,
      });
      for (const file of files) {
        await attachToTarget(token, file, {
          entityType: "VERIFICATION",
          entityId: raised.id,
          purpose: "GENERAL",
        });
      }
      onRaised(raised.humanId);
    } catch (caught) {
      if (caught instanceof ApiError) {
        shortNotice.note(caught);
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return <></>;
  }

  return (
    <ActionDialog open title={strings.itps.raiseFreeRfiHeading} onClose={onClose} error={error}>
      <p className="q-shell__lede">{strings.itps.raiseFreeRfiLede}</p>
      <p className="q-note" data-testid="free-rfi-notice">
        {strings.itps.freeRfiPlainNotice}
      </p>
      <div className="q-field">
        <label className="q-field__label" htmlFor="free-rfi-contract">
          {strings.itps.freeRfiContractLabel}
        </label>
        <ServerField field="contract_id">
          <select
            id="free-rfi-contract"
            className="q-input"
            data-testid="free-rfi-contract"
            value={selectedContractId}
            onChange={(event) => {
              setContractId(event.target.value);
            }}
          >
            <option value="">{strings.itps.freeRfiContractChoose}</option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.humanId} ({c.contractorPartyName})
              </option>
            ))}
          </select>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="free-rfi-discipline">
          {strings.itps.freeRfiDisciplineLabel}
        </label>
        <ServerField field="discipline_id">
          <select
            id="free-rfi-discipline"
            className="q-input"
            data-testid="free-rfi-discipline"
            value={disciplineId}
            onChange={(event) => {
              setDisciplineId(event.target.value);
            }}
          >
            <option value="">{strings.itps.freeRfiDisciplineChoose}</option>
            {disciplines.map((d) => (
              <option key={d.token} value={d.id ?? d.token}>
                {d.label}
              </option>
            ))}
          </select>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="free-rfi-category">
          {strings.itps.rfiCategoryLabel}
        </label>
        <p className="q-note" id="free-rfi-category-hint">
          {engineeringOnly ? strings.itps.freeRfiEngineeringOnly : strings.itps.freeRfiCategoryHint}
        </p>
        <ServerField field="checklist_category">
          <select
            id="free-rfi-category"
            className="q-input"
            data-testid="free-rfi-category"
            aria-describedby="free-rfi-category-hint"
            value={category}
            onChange={(event) => {
              setCategory(event.target.value);
            }}
          >
            <option value="">{strings.itps.rfiCategoryDefault}</option>
            {categories.map((one) => (
              <option key={one.token} value={one.token}>
                {one.label}
              </option>
            ))}
          </select>
        </ServerField>
      </div>
      <RfiInstanceFields
        prefix="free-rfi"
        required={false}
        extras={extras}
        error={error}
        onChange={setExtras}
      />
      <div className="q-field">
        <label className="q-field__label" htmlFor="free-rfi-scope">
          {strings.itps.rfiScopeLabel}
        </label>
        <ServerField field="scope">
          <textarea
            id="free-rfi-scope"
            className="q-textarea"
            data-testid="free-rfi-scope"
            value={scope}
            onChange={(event) => {
              setScope(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="free-rfi-date">
          {strings.itps.rfiDateLabel}
        </label>
        <ServerField field="proposed_date">
          <input
            id="free-rfi-date"
            className="q-input"
            type="date"
            data-testid="free-rfi-date"
            value={proposedDate}
            onChange={(event) => {
              setProposedDate(event.target.value);
            }}
          />
        </ServerField>
      </div>
      {shortNotice.asked && (
        <RfiShortNoticeField prefix="free-rfi" extras={extras} onChange={setExtras} />
      )}
      <div className="q-field">
        <label className="q-field__label" htmlFor="free-rfi-location">
          {strings.itps.rfiLocationLabel}
        </label>
        <ServerField field="location">
          <input
            id="free-rfi-location"
            className="q-input"
            data-testid="free-rfi-location"
            value={location}
            onChange={(event) => {
              setLocation(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="free-rfi-attachments">
          {strings.itps.freeRfiAttachmentsLabel}
        </label>
        <input
          id="free-rfi-attachments"
          type="file"
          multiple
          data-testid="free-rfi-attachments"
          onChange={(event) => {
            if (event.target.files) {
              const selected = Array.from(event.target.files);
              setFiles((prev) => [...prev, ...selected]);
            }
          }}
        />
        {files.length > 0 && (
          <ul className="q-gap-top" data-testid="free-rfi-files-list">
            {files.map((file, idx) => (
              <li key={`${file.name}-${String(idx)}`} className="q-note">
                {file.name} ({String(Math.max(1, Math.round(file.size / 1024)))} kB){" "}
                <button
                  type="button"
                  className="q-button q-button--quiet"
                  data-testid={`free-rfi-remove-file-${String(idx)}`}
                  onClick={() => {
                    setFiles((prev) => prev.filter((_, i) => i !== idx));
                  }}
                >
                  {strings.itps.freeRfiRemoveFile}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <RfiDeclarationFields
        prefix="free-rfi"
        extras={extras}
        declared={declared}
        withEvidence={false}
        onChange={setExtras}
        onDeclared={setDeclared}
      />
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="free-rfi-send"
          disabled={busy || !ready}
          title={declared ? undefined : strings.itps.rfiDeclarationNeeded}
          onClick={() => {
            void send();
          }}
        >
          {shortNotice.asked ? strings.itps.rfiSendShortNotice : strings.itps.rfiSend}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

/** The files one review move carries, downloaded through the gated API. */
function MoveAttachments({ moveId }: { moveId: string }): React.JSX.Element | null {
  const token = useToken();
  const attachments = useQuery(
    () => listTargetAttachments(token, { entityType: "ITP_REVIEW_MOVE", entityId: moveId }),
    [token, moveId],
  );
  if (attachments.kind !== "ready" || attachments.data.length === 0) {
    return null;
  }
  return (
    <div className="q-attachments" data-testid="move-attachments">
      {attachments.data.map((entry: TargetAttachment) => (
        <p className="q-attachment" key={entry.linkId} data-testid="move-attachment">
          <DownloadLink attachmentId={entry.attachmentId} filename={entry.filename} /> (
          {String(Math.max(1, Math.round(entry.fileSizeBytes / 1024)))} kB)
        </p>
      ))}
    </div>
  );
}

function PhaseHistory({ entries }: { entries: ItpTransitionEntry[] }): React.JSX.Element {
  if (entries.length === 0) {
    return <p className="q-note">{strings.common.empty}</p>;
  }
  return (
    <div className="q-thread">
      {entries.map((entry) => (
        <article className="q-thread__entry" key={entry.id}>
          <p className="q-thread__meta">
            <strong>{term(entry.event)}</strong> · {term(entry.fromState)} → {term(entry.toState)} ·{" "}
            {day(entry.at)}
            {entry.actorDisplayName !== null && ` · ${entry.actorDisplayName}`}
          </p>
          {entry.comment !== null && <p className="q-thread__body">{entry.comment}</p>}
        </article>
      ))}
    </div>
  );
}

/** Kept for the reload counter's type stability; no behaviour hangs on it. */
export type { ItpItem };
